<template>
  <div
    class="w-full max-w-full min-w-0 bg-white"
    :class="{
      'border rounded-lg': !embedded,
      'overflow-hidden': !rescheduleMode,
      'activities-weekly-table--reschedule': rescheduleMode,
    }"
    :style="rootStyle"
  >
    <div v-if="isLoadingActivities && !activityRows.length" class="py-8 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>
    <template v-else>
    <!-- Barra resumen -->
    <div
      v-if="!embedded"
      class="flex flex-wrap items-center justify-between gap-2 py-1 px-3 text-sm border-b"
      style="background-color: #eef3fb; border-color: #c5d4eb;"
    >
      <div class="flex items-center gap-1.5 font-semibold" style="color: #3b66bc;">
        <b-icon pack="fas" icon="list" size="is-small" />
        <span
          class="inline-flex items-center py-px px-1 rounded-sm text-white font-bold uppercase"
          style="background-color: #3b66bc; font-size: 10px;"
        >{{ summaryBadgeLabel }}</span>
        <span>{{ summaryCounts }}</span>
      </div>
      <div class="font-medium text-grey-darker">
        {{ weekSummaryLabel }}
      </div>
    </div>

    <!-- Grupos / filtros -->
    <div v-if="!embedded" class="flex flex-wrap items-center gap-1.5 py-2 px-4 border-b" style="border-color: #c5d4eb;">
      <span class="inline-flex items-center gap-1 mr-1 text-xs font-bold uppercase text-gray-500">
        <b-icon pack="fas" icon="eye" size="is-small" />
        {{ $t('groups') }}
      </span>
      <button
        v-for="group in groupFiltersWithCounts"
        :key="group.key"
        type="button"
        class="activities-weekly-table__group-chip inline-flex items-center gap-1.5 py-0.5 px-2 border-0 rounded-full text-white text-xs font-bold cursor-pointer"
        :class="{ 'activities-weekly-table__group-chip--hidden': isGroupHidden(group.columnGroup) }"
        :style="{ backgroundColor: group.color }"
        :aria-pressed="isGroupHidden(group.columnGroup)"
        @click="toggleColumnGroup(group.columnGroup)"
      >
        {{ $t(group.labelKey) }}
        <span class="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-white text-gray-800 text-xs font-bold">{{ group.count }}</span>
      </button>
    </div>

    <div
      class="activities-weekly-table__scroll relative w-full max-w-full min-w-0"
      :class="rescheduleMode ? 'activities-weekly-table__scroll--reschedule' : 'overflow-x-auto'"
    >
    <table class="activities-weekly-table__grid w-full text-xs border-collapse min-w-max">
      <thead>
        <tr class="activities-weekly-table__group-row">
          <th
            v-for="group in visibleColumnGroups"
            :key="group.key"
            :colspan="groupColspan(group)"
            class="px-2 py-2 text-center text-white font-bold uppercase tracking-wide activities-weekly-table__header-cell"
            :class="group.headerClass"
          >
            {{ group.labelKey ? $t(group.labelKey) : group.label }}
          </th>
        </tr>
        <tr class="activities-weekly-table__column-row">
          <th class="activities-weekly-table__expand-header activities-weekly-table__header-cell activities-weekly-table__column-header">
          </th>
          <th
            v-if="showActivityActionsColumn"
            class="activities-weekly-table__actions-col activities-weekly-table__header-cell activities-weekly-table__column-header"
          />
          <th
            v-for="column in visibleFlatColumns"
            :key="column.key"
            class="px-2 py-2 text-center text-white font-semibold whitespace-nowrap normal-case activities-weekly-table__header-cell activities-weekly-table__column-header"
          >
            <span>{{ column.labelKey ? $t(column.labelKey) : column.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody v-if="!activityRows.length">
        <tr>
          <td
            :colspan="totalColumnCount"
            class="px-4 py-8 text-center text-gray-500 border border-gray-300 bg-grey-lightest"
          >
            Sin resultados
          </td>
        </tr>
      </tbody>
      <tbody
        v-for="(row, rowIndex) in activityRows"
        v-else
        :key="rowKey(row, rowIndex)"
      >
          <tr
            class="activities-weekly-table__data-row"
            :class="{
              'activities-weekly-table__row--expanded': isRowExpanded(row, rowIndex),
              'activities-weekly-table__row--manual': isManuallyCreated(row),
            }"
          >
            <!-- detailed -->
            <td class="px-1 py-1 border border-gray-300 text-center align-middle bg-white">
              <button
                type="button"
                class="inline-flex items-center justify-center w-7 h-7 text-blue-light hover:text-blue focus:outline-none"
                :aria-expanded="isRowExpanded(row, rowIndex)"
                :aria-label="isRowExpanded(row, rowIndex) ? 'Ocultar detalle' : 'Mostrar detalle'"
                @click="toggleRowDetail(row, rowIndex)"
              >
                <b-icon
                  pack="fas"
                  :icon="isRowExpanded(row, rowIndex) ? 'chevron-down' : 'chevron-right'"
                  size="is-small"
                />
              </button>
            </td>

            <!-- Acciones -->
            <td
              v-if="showActivityActionsColumn"
              class="px-1 py-1 border border-gray-300 text-center align-middle activities-weekly-table__actions-col"
            >
              <div v-if="canEditActivityInModal(row)" class="text-center cursor-pointer" @click.stop>
                <b-dropdown
                  aria-role="list"
                  position="is-bottom-right"
                  append-to-body
                >
                  <template #trigger="{ active }">
                    <b-icon
                      :icon="active ? 'ellipsis-h' : 'ellipsis-v'"
                      pack="fas"
                      size="is-small"
                      class="text-grey-dark"
                    />
                  </template>
                  <b-dropdown-item
                    aria-role="listitem"
                    @click="openEditActivityInModal(row)"
                  >
                    <span class="text-xs">{{ $t('edit') }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </td>

            <!-- Código -->
            <td v-if="isActivityColumnVisible('code')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.code)" class="activities-weekly-table__empty">x</span>
              <b-tooltip
                v-else-if="isManuallyCreated(row)"
                :label="$t('activity_created_manually')"
                position="is-right"
                type="is-dark"
              >
                <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full text-purple-dark bg-purple-lightest border-purple-lighter">
                  <b-icon pack="fas" icon="pen" size="is-small" />
                  {{ row.code }}
                </span>
              </b-tooltip>
              <span v-else>{{ row.code }}</span>
              <div v-if="isManuallyCreated(row) && row.created_at" class="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                <b-icon pack="far" icon="calendar-alt" size="is-small" />
                {{ $t('created') }} {{ row.created_at }}
              </div>
            </td>

            <!-- KKS -->
            <td v-if="isActivityColumnVisible('kks')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.kks)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ row.kks }}</span>
            </td>

            <!-- Grupo -->
            <td v-if="isActivityColumnVisible('group')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <div
                v-if="row.group"
                class="relative inline-flex items-center justify-center cursor-pointer w-7 h-7 overflow-hidden rounded-full mx-auto"
                :style="{ backgroundColor: colorGroup(row.group) }"
              >
                <b-tooltip
                  :label="groupTooltip(row)"
                  position="is-right"
                >
                  <span class="font-medium text-white text-xs">{{ nameGroup(row.group) }}</span>
                  <template v-slot:content>
                    <div><b>Grupo:</b> {{ row.group.name }}</div>
                  </template>
                </b-tooltip>
              </div>
              <span v-else>—</span>
            </td>

            <!-- Nombre -->
            <td v-if="isActivityColumnVisible('name')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle activities-weekly-table__name-td">
              <div
                class="activities-weekly-table__name-cell"
                :class="{ 'activities-weekly-subtable__name-cell--stacked': isActivityRescheduleMode && shouldShowSubactivityStatus(row) }"
              >
                <div class="activities-weekly-subtable__name-top activities-weekly-table__name-top">
                  <button
                    v-if="isActivityRescheduleMode"
                    type="button"
                    class="activities-weekly-subtable__pin-icon"
                    :class="{ 'activities-weekly-subtable__pin-icon--pending': isPendingRescheduling(row) }"
                    :aria-label="isPendingRescheduling(row) ? $t('activity_status_pending_rescheduling') : $t('reschedule')"
                    @click.stop="toggleActivityPendingPin(row)"
                  >
                  <!-- pin de la actividad para reprogramacion -->
                    <b-icon pack="fas" icon="thumbtack" size="is-small" />
                  </button>
                  <span
                    class="activities-weekly-table__name-text"
                    :class="{ 'activities-weekly-table__name-text--clickable': canNavigateToRow(row) }"
                    @click="onNameClicked(row)"
                  >{{ row.name || '—' }}</span>
                </div>
                <!-- estado de la actividad -->
                <span
                  v-if="isActivityRescheduleMode && shouldShowSubactivityStatus(row)"
                  class="activities-weekly-subtable__status"
                  :class="subactivityStatusClass(row)"
                >
                  <b-icon
                    v-if="isPendingRescheduling(row)"
                    pack="fas"
                    icon="thumbtack"
                    size="is-small"
                    class="text-current"
                  />
                  {{ subactivityStatusLabel(row) }}
                </span>
                <b-tooltip
                  v-if="row.name && showMeasurementsIcon(row)"
                  :label="measurementsTooltip(row)"
                  position="is-left"
                >
                <!-- vinculaciones de boq roots -->
                  <span
                    class="activities-weekly-table__ruler-icon relative inline-flex flex-shrink-0"
                    :class="{ 'activities-weekly-table__ruler-icon--clickable': canOpenLinkedBoqRoots(row) }"
                    @click.stop="openLinkedBoqRootsModal(row)"
                  >
                    <span class="inline-flex items-center justify-center w-7 h-7 border border-gray-300 rounded bg-grey-lightest text-grey-dark">
                      <b-icon pack="fas" icon="ruler-combined" size="is-small" />
                    </span>
                    <span
                      v-if="measurementsBadgeCount(row)"
                      class="activities-weekly-table__ruler-badge"
                    >
                      {{ measurementsBadgeCount(row) }}
                    </span>
                  </span>
                </b-tooltip>
                <b-tooltip
                  v-if="isActivityLikeProgressMode && row.name"
                  :label="$t('weekly_edit_activity_title')"
                  position="is-left"
                >
                  <button
                    type="button"
                    class="activities-weekly-table__edit-icon inline-flex flex-shrink-0"
                    :aria-label="$t('weekly_edit_activity_title')"
                    @click.stop="openEditActivityModal(row)"
                  >
                    <span class="inline-flex items-center justify-center w-7 h-7 border border-gray-300 rounded bg-grey-lightest text-grey-dark">
                      <b-icon pack="fas" icon="pen" size="is-small" />
                    </span>
                  </button>
                </b-tooltip>
              </div>
            </td>

            <template v-if="isGroupVisible('tracking')">
            <!-- Predecesoras -->
            <td v-if="isActivityColumnVisible('predecessors')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle activities-weekly-table__relations-td">
              <div class="activities-weekly-table__relations-cell">
                <template v-if="relationCodes(row.predecessors).length">
                  <span
                    v-for="(code, codeIndex) in visibleRelationCodes(row, 'predecessors')"
                    :key="`predecessors-${codeIndex}-${code}`"
                    class="activities-weekly-table__chip"
                  >
                    {{ code }}
                  </span>
                  <button
                    v-if="hiddenRelationCount(row, 'predecessors') > 0 || isRelationExpanded(row, 'predecessors')"
                    type="button"
                    class="activities-weekly-table__chip activities-weekly-table__chip--more"
                    :title="isRelationExpanded(row, 'predecessors') ? $t('hide') : $t('see_more')"
                    @click.stop="toggleRelationExpand(row, 'predecessors')"
                  >
                    <template v-if="isRelationExpanded(row, 'predecessors')">
                      {{ $t('hide') }}
                    </template>
                    <template v-else>
                      {{ $t('see_more') }}
                      <span class="activities-weekly-table__chip-more-count">
                        +{{ hiddenRelationCount(row, 'predecessors') }}
                      </span>
                    </template>
                  </button>
                </template>
                <span v-else>—</span>
              </div>
            </td>

            <!-- Sucesoras -->
            <td v-if="isActivityColumnVisible('successors')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle activities-weekly-table__relations-td">
              <div class="activities-weekly-table__relations-cell">
                <template v-if="relationCodes(row.successors).length">
                  <span
                    v-for="(code, codeIndex) in visibleRelationCodes(row, 'successors')"
                    :key="`successors-${codeIndex}-${code}`"
                    class="activities-weekly-table__chip"
                  >
                    {{ code }}
                  </span>
                  <button
                    v-if="hiddenRelationCount(row, 'successors') > 0 || isRelationExpanded(row, 'successors')"
                    type="button"
                    class="activities-weekly-table__chip activities-weekly-table__chip--more"
                    :title="isRelationExpanded(row, 'successors') ? $t('hide') : $t('see_more')"
                    @click.stop="toggleRelationExpand(row, 'successors')"
                  >
                    <template v-if="isRelationExpanded(row, 'successors')">
                      {{ $t('hide') }}
                    </template>
                    <template v-else>
                      {{ $t('see_more') }}
                      <span class="activities-weekly-table__chip-more-count">
                        +{{ hiddenRelationCount(row, 'successors') }}
                      </span>
                    </template>
                  </button>
                </template>
                <span v-else>—</span>
              </div>
            </td>

            <!-- Línea base (scheduled) -->
            <td v-if="isActivityColumnVisible('baseline_line')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle w-32 vertical-center text-center">
              <progress-bar
                v-if="canProgress"
                :step="parseFloat(row.baseline_line) || 0"
                :decimals="displayDecimals"
                color="blue-light"
                class="w-full"
              />
              <span v-else class="activities-weekly-table__empty">—</span>
            </td>

            <!-- Última planificación (current) -->
            <td v-if="isActivityColumnVisible('latest_planning')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle w-32 vertical-center text-center">
              <progress-bar
                v-if="canProgress"
                :step="parseFloat(row.latest_planning) || 0"
                :decimals="displayDecimals"
                color="blue-light"
                class="w-full"
              />
              <span v-else class="activities-weekly-table__empty">—</span>
            </td>

            <!-- Progreso -->
            <td v-if="isActivityColumnVisible('progress')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle w-32 vertical-center text-center">
              <progress-bar
                :step="parseFloat(row.progress) || 0"
                color="blue-light"
                class="w-full"
              />
            </td>

            <!-- Última línea registrada en las mediciones (solo modo free) -->
            <td v-if="isFreeBoqRootMode && isActivityColumnVisible('last_assignment_date')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.last_assignment_date)" class="activities-weekly-table__empty">—</span>
              <span v-else>{{ row.last_assignment_date }}</span>
            </td>

            <!-- Progreso P6 (Primavera) -->
            <td v-if="isActivityColumnVisible('p6_progress')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle w-32 vertical-center text-center">
              <progress-bar
                v-if="row.p6_progress != null"
                :step="parseFloat(row.p6_progress) || 0"
                color="blue-light"
                class="w-full"
              />
              <span v-else class="activities-weekly-table__empty">—</span>
            </td>

            <!-- Peso -->
            <td v-if="isActivityColumnVisible('weight')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span class="activities-weekly-table__weight">
                {{ parseFloat(row.weight) || 0 }}%
              </span>
            </td>

            <!-- Cantidad física -->
            <td v-if="isActivityColumnVisible('physical_quantity')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.physical_quantity)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatBoqTrackingQuantity(row.physical_quantity, row) }}</span>
            </td>

            <!-- Realizado -->
            <td v-if="isActivityColumnVisible('tracking_done')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.tracking_done)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatBoqTrackingQuantity(row.tracking_done, row) }}</span>
            </td>

            <!-- Pendiente -->
            <td v-if="isActivityColumnVisible('tracking_pending')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.tracking_pending)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatBoqTrackingQuantity(row.tracking_pending, row) }}</span>
            </td>

            <!-- HH programadas -->
            <td v-if="isActivityColumnVisible('tracking_scheduled_hours')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.tracking_scheduled_hours)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatWeeklyManHours(row.tracking_scheduled_hours) + ' h' }}</span>
            </td>

            <!-- HH realizadas -->
            <td v-if="isActivityColumnVisible('tracking_done_hours')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.tracking_done_hours)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatWeeklyManHours(row.tracking_done_hours) + ' h' }}</span>
            </td>

            <!-- HH pendientes -->
            <td v-if="isActivityColumnVisible('tracking_pending_hours')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              <span v-if="isCellEmpty(row.tracking_pending_hours)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatWeeklyManHours(row.tracking_pending_hours) + ' h' }}</span>
            </td>

            <!-- Supervisor -->
            <td v-if="isActivityColumnVisible('supervisor')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle whitespace-nowrap">
              {{ row.supervisor || '—' }}
            </td>

            <!-- Encargado -->
            <td
              v-if="isActivityColumnVisible('manager')"
              class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap activities-weekly-subtable__cell--manager"
            >
              <b-autocomplete
                v-if="isActivityRescheduleMode"
                :ref="`managerAc_${row.id}`"
                v-model="row.manager_search"
                :data="row.managerOptions || emptyManagerOptions"
                field="fullname"
                open-on-focus
                clearable
                append-to-body
                size="is-small"
                :placeholder="$t('select_manager')"
                class="activities-weekly-subtable__inline-autocomplete"
                @focus="onManagerFocus(row)"
                @typing="onManagerTyping(row, $event)"
                @select="onManagerSelected(row, $event)"
                @active="onManagerAutocompleteActive(row, $event)"
              >
                <template v-slot="props">
                  <div class="text-xs">
                    <span class="font-semibold">{{ props.option.fullname }}</span>
                    <span v-if="props.option.email" class="text-grey-dark"> ({{ props.option.email }})</span>
                  </div>
                </template>
                <template #empty>{{ $t('no_results') }}</template>
              </b-autocomplete>
              <span v-else>{{ row.manager || '—' }}</span>
            </td>
            </template>

            <template v-if="isGroupVisible('planned_advance')">
            <!-- Personas -->
            <td
              v-if="isActivityColumnVisible('people')"
              class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap"
            >
              <b-input
                v-if="isActivityRescheduleMode"
                v-model.number="row.people"
                type="number"
                min="0"
                size="is-small"
                class="activities-weekly-subtable__inline-input"
                @input="markActivityRescheduled(row)"
              />
              <template v-else>
                <span v-if="isCellEmpty(row.people)" class="activities-weekly-table__empty">x</span>
                <span v-else>{{ row.people }}</span>
              </template>
            </td>

            <!-- avance planificado: Inicio planificado -->
            <td v-if="isActivityColumnVisible('scheduled_start_date')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_start_date)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ row.scheduled_start_date }}</span>
            </td>

            <!-- avance planificado: Fin planificado -->
            <td v-if="isActivityColumnVisible('scheduled_end_date')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_end_date)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ row.scheduled_end_date }}</span>
            </td>

            <!-- avance planificado: Duración plan. -->
            <td v-if="isActivityColumnVisible('scheduled_duration_days')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_duration_days)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatDuration(row.scheduled_duration_days) }}</span>
            </td>

            <!-- avance planificado: Float plan. -->
            <td v-if="isActivityColumnVisible('planned_float')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.planned_float)" class="activities-weekly-table__empty">x</span>
              <span v-else :class="floatColorClass(row.planned_float)">{{ formatDuration(row.planned_float) }}</span>
            </td>

            <!-- avance planificado: Avance acum. plan. -->
            <td v-if="isActivityColumnVisible('planned_progress')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.planned_progress)" class="activities-weekly-table__empty">x</span>
              <span v-else class="font-bold">{{ formatBoqTrackingQuantity(row.planned_progress, row) }}</span>
            </td>

            <!-- avance planificado: KPI -->
            <td v-if="isActivityColumnVisible('planned_kpi')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.planned_kpi)" class="activities-weekly-table__empty">x</span>
              <span
                v-else
                class="activities-weekly-table__kpi-pill"
                :class="kpiPillClass(row.planned_kpi)"
              >
                {{ formatKpi(row.planned_kpi) }}
              </span>
            </td>
            </template>

            <template v-if="isGroupVisible('scheduled_advance')">
            <!-- avance programado: Inicio programado -->
            <td v-if="isActivityColumnVisible('current_start_date')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <b-datepicker
                v-if="isActivityRescheduleMode"
                v-model="row._startDate"
                size="is-small"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :min-date="activityPlannedDateMin(row)"
                :max-date="activityStartMaxDate(row)"
                class="activities-weekly-subtable__inline-datepicker"
                @input="onActivityStartDateChange(row, $event)"
              />
              <template v-else>
                <span v-if="isCellEmpty(row.current_start_date)" class="activities-weekly-table__empty">x</span>
                <span v-else>{{ row.current_start_date }}</span>
              </template>
            </td>

            <!-- avance programado: Fin programado -->
            <td v-if="isActivityColumnVisible('current_end_date')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <b-datepicker
                v-if="isActivityRescheduleMode"
                v-model="row._endDate"
                size="is-small"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :min-date="activityEndMinDate(row)"
                :max-date="activityPlannedDateMax(row)"
                class="activities-weekly-subtable__inline-datepicker"
                @input="onActivityEndDateChange(row, $event)"
              />
              <template v-else>
                <span v-if="isCellEmpty(row.current_end_date)" class="activities-weekly-table__empty">x</span>
                <span v-else>{{ row.current_end_date }}</span>
              </template>
            </td>

            <!-- avance programado: Duración prog. -->
            <td v-if="isActivityColumnVisible('current_duration_days')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.current_duration_days)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatDuration(row.current_duration_days) }}</span>
            </td>

            <!-- avance programado: Float prog. -->
            <td v-if="isActivityColumnVisible('scheduled_float')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_float)" class="activities-weekly-table__empty">x</span>
              <span v-else :class="floatColorClass(row.scheduled_float)">{{ formatDuration(row.scheduled_float) }}</span>
            </td>

            <!-- avance programado: Avance acum. prog. -->
            <td v-if="isActivityColumnVisible('scheduled_progress')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_progress)" class="activities-weekly-table__empty">x</span>
              <span v-else class="font-bold">{{ formatBoqTrackingQuantity(row.scheduled_progress, row) }}</span>
            </td>

            <!-- avance programado: KPI -->
            <td v-if="isActivityColumnVisible('scheduled_kpi')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.scheduled_kpi)" class="activities-weekly-table__empty">x</span>
              <span
                v-else
                class="activities-weekly-table__kpi-pill"
                :class="kpiPillClass(row.scheduled_kpi)"
              >
                {{ formatKpi(row.scheduled_kpi) }}
              </span>
            </td>
            </template>

            <template v-if="isGroupVisible('real')">
            <!-- Real: Inicio real -->
            <td v-if="isActivityColumnVisible('real_start')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.real_start)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ row.real_start }}</span>
            </td>

            <!-- Real: Fin real -->
            <td v-if="isActivityColumnVisible('real_end')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.real_end)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ row.real_end }}</span>
            </td>

            <!-- Real: Duración real -->
            <td v-if="isActivityColumnVisible('real_duration_days')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.real_duration_days)" class="activities-weekly-table__empty">x</span>
              <span v-else>{{ formatDuration(row.real_duration_days) }}</span>
            </td>

            <!-- Real: Float real -->
            <td v-if="isActivityColumnVisible('real_float')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.real_float)" class="activities-weekly-table__empty">x</span>
              <span v-else :class="floatColorClass(row.real_float)">{{ formatDuration(row.real_float) }}</span>
            </td>

            <!-- Real: KPI -->
            <td v-if="isActivityColumnVisible('real_kpi')" class="px-2 py-1 border border-gray-300 text-gray-800 align-middle text-center whitespace-nowrap">
              <span v-if="isCellEmpty(row.real_kpi)" class="activities-weekly-table__empty">x</span>
              <span
                v-else
                class="activities-weekly-table__kpi-pill"
                :class="kpiPillClass(row.real_kpi)"
              >
                {{ formatKpi(row.real_kpi) }}
              </span>
            </td>
            </template>
          </tr>
          <tr v-if="isRowExpanded(row, rowIndex)" class="activities-weekly-table__detail-row">
            <td
              :colspan="totalColumnCount"
              class="px-0 pt-0 pb-3 border border-gray-300 border-t-0 bg-white activities-weekly-table__detail-cell"
            >
              <div
                class="activities-weekly-table__detail-panel"
                :class="{ 'activities-weekly-table__detail-panel--reschedule': rescheduleMode }"
              >
                <!-- Modo free: cabecera fija con crear subactividad / medición -->
                <template v-if="isFreeBoqRootMode">
                  <div class="activities-weekly-subtable__toolbar">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="activities-weekly-subtable__level-badge">NIVEL 2</span>
                      <!-- Crear subactividad en modo free: desactivado por ahora, se conserva por si se necesita en el futuro
                      <button
                        type="button"
                        class="activities-weekly-subtable__add-btn"
                        @click="openCreateSubactivityModal(row)"
                      >
                        <b-icon pack="fas" icon="plus" size="is-small" />
                        <span>{{ $t('create_subactivity') }}</span>
                      </button>
                      -->
                      <button
                        type="button"
                        class="activities-weekly-subtable__add-btn"
                        :disabled="freeModeHasSubactivities(row) || isEditingMeasurements(row) || isDeletingMeasurements(row)"
                        @click="openCreateMeasurementForFreeMode(row)"
                      >
                        <b-icon pack="fas" icon="plus" size="is-small" />
                        <span>{{ $t('create_measurement') }}</span>
                      </button>
                      <button
                        v-if="canEditMeasurements(row)"
                        type="button"
                        class="activities-weekly-subtable__add-btn"
                        :class="{ 'activities-weekly-subtable__add-btn--red': isEditingMeasurements(row) }"
                        :disabled="freeModeHasSubactivities(row) || !Number(row.assignments_num || 0) || isDeletingMeasurements(row)"
                        @click="toggleEditMeasurements(row)"
                      >
                        <b-icon pack="fas" :icon="isEditingMeasurements(row) ? 'times' : 'edit'" size="is-small" />
                        <span>{{ isEditingMeasurements(row) ? $t('cancel') : $t('edit_assignments') }}</span>
                      </button>
                      <button
                        v-if="canDeleteMeasurements(row)"
                        type="button"
                        class="activities-weekly-subtable__add-btn activities-weekly-subtable__add-btn--danger"
                        :class="{ 'activities-weekly-subtable__add-btn--red': isDeletingMeasurements(row) }"
                        :disabled="freeModeHasSubactivities(row) || !Number(row.assignments_num || 0) || isEditingMeasurements(row)"
                        @click="toggleDeleteMeasurements(row)"
                      >
                        <b-icon pack="fas" :icon="isDeletingMeasurements(row) ? 'times' : 'trash-alt'" size="is-small" />
                        <span>{{ isDeletingMeasurements(row) ? $t('cancel') : $t('delete_assignments') }}</span>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Subactividades (modo subactivity o free con subactividades) -->
                <template v-if="displaySubactivities(row)">
                  <div class="activities-weekly-subtable__body">
                    <div v-if="!isFreeBoqRootMode" class="activities-weekly-subtable__toolbar">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="activities-weekly-subtable__level-badge">NIVEL 2</span>
                        <button
                          type="button"
                          class="activities-weekly-subtable__add-btn"
                          @click="openCreateSubactivityModal(row)"
                        >
                          <b-icon pack="fas" icon="plus" size="is-small" />
                          <span>{{ $t('create_subactivity') }}</span>
                        </button>
                      </div>
                    </div>

                    <div class="activities-weekly-subtable__section">
                      <div class="activities-weekly-subtable__section-header">
                        <b-icon
                          pack="fas"
                          icon="sitemap"
                          size="is-small"
                          class="activities-weekly-subtable__section-icon"
                        />
                        <span class="activities-weekly-subtable__section-title">
                          {{ $t('weekly_subactivities_associated_title', {
                            count: getSubactivitiesForRow(row).length
                          }) }}
                        </span>
                      </div>

                      <div
                        v-if="isSubactivitiesLoading(row) && !getSubactivitiesForRow(row).length"
                        class="p-4 text-sm text-center text-gray-500"
                      >
                        {{ $t('loading') }}...
                      </div>
                      <div
                        v-else
                        :class="rescheduleMode ? 'activities-weekly-subtable__scroll--reschedule' : 'overflow-x-auto'"
                      >
                        <table
                          class="activities-weekly-subtable w-full text-xs border-collapse min-w-max"
                          :class="{ 'activities-weekly-subtable--reschedule': rescheduleMode }"
                        >
                      <thead>
                        <tr>
                          <th class="activities-weekly-subtable__header activities-weekly-subtable__expand-col" />
                          <th class="activities-weekly-subtable__header activities-weekly-subtable__actions-col" />
                          <th
                            v-for="column in subactivityTableColumns"
                            :key="column.key"
                            class="activities-weekly-subtable__header"
                            :class="{
                              'activities-weekly-subtable__header--left': column.align === 'left',
                            }"
                          >
                            {{ $t(column.labelKey) }}
                          </th>
                        </tr>
                      </thead>
                      <tbody v-if="!getSubactivitiesForRow(row).length">
                        <tr>
                          <td
                            :colspan="subactivityTableColspan"
                            class="activities-weekly-subtable__cell px-4 py-6 text-center text-gray-500"
                          >
                            {{ $t('no_results') }}
                          </td>
                        </tr>
                      </tbody>
                      <tbody
                        v-for="(sub, subIndex) in getSubactivitiesForRow(row)"
                        v-else
                        :key="subactivityRowKey(sub, subIndex)"
                      >
                        <tr
                          class="hover:bg-blue-lightest"
                          :class="{
                            'activities-weekly-subtable__row--expanded': isSubactivityExpanded(row, sub, subIndex),
                            'activities-weekly-subtable__row--level-3': isSubactivityExpanded(row, sub, subIndex),
                            'activities-weekly-subtable__row--manual': isManuallyCreated(sub),
                          }"
                        >
                        <!-- Expand → mediciones (nivel 3) -->
                        <td class="activities-weekly-subtable__cell activities-weekly-subtable__expand-col text-center">
                          <button
                            type="button"
                            class="inline-flex items-center justify-center w-7 h-7 text-blue-light hover:text-blue focus:outline-none"
                            :aria-expanded="isSubactivityExpanded(row, sub, subIndex)"
                            :aria-label="isSubactivityExpanded(row, sub, subIndex) ? 'Ocultar mediciones' : 'Mostrar mediciones'"
                            @click="toggleSubactivityDetail(row, sub, subIndex)"
                          >
                            <b-icon
                              pack="fas"
                              :icon="isSubactivityExpanded(row, sub, subIndex) ? 'chevron-down' : 'chevron-right'"
                              size="is-small"
                            />
                          </button>
                        </td>

                        <!-- Acciones -->
                        <td class="activities-weekly-subtable__cell activities-weekly-subtable__actions-col text-center">
                          <div v-if="hasSubactivityActions(sub)" class="text-center cursor-pointer" @click.stop>
                            <b-dropdown
                              aria-role="list"
                              position="is-bottom-right"
                              append-to-body
                            >
                              <template #trigger="{ active }">
                                <b-icon
                                  :icon="active ? 'ellipsis-h' : 'ellipsis-v'"
                                  pack="fas"
                                  size="is-small"
                                  class="text-grey-dark"
                                />
                              </template>
                              <b-dropdown-item
                                v-if="canEditSubactivityInModal(sub)"
                                aria-role="listitem"
                                @click="openEditSubactivityModal(row, sub)"
                              >
                                <span class="text-xs">{{ $t('edit') }}</span>
                              </b-dropdown-item>
                              <b-dropdown-item
                                v-if="!isFreeBoqRootMode"
                                aria-role="listitem"
                                @click="openAdminAssignmentsModal(row, sub)"
                              >
                                <span class="text-xs">{{ $t('admin_assignments') }}</span>
                              </b-dropdown-item>
                            </b-dropdown>
                          </div>
                        </td>

                        <!-- Código -->
                        <td v-if="isSubactivityColumnVisible('code')" class="activities-weekly-subtable__cell whitespace-nowrap">
                          <b-tooltip
                            v-if="isManuallyCreated(sub)"
                            :label="$t('activity_created_manually')"
                            position="is-right"
                            type="is-dark"
                          >
                            <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full text-purple-dark bg-purple-lightest border-purple-lighter">
                              <b-icon pack="fas" icon="pen" size="is-small" />
                              {{ sub.code }}
                            </span>
                          </b-tooltip>
                          <template v-else>{{ sub.code }}</template>
                          <div v-if="isManuallyCreated(sub) && sub.created_at" class="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                            <b-icon pack="far" icon="calendar-alt" size="is-small" />
                            {{ $t('created') }} {{ sub.created_at }}
                          </div>
                        </td>

                        <!-- Nombre + estado -->
                        <td v-if="isSubactivityColumnVisible('name')" class="activities-weekly-subtable__cell">
                          <div
                            class="activities-weekly-subtable__name-cell"
                            :class="{ 'activities-weekly-subtable__name-cell--stacked': shouldShowSubactivityStatus(sub) }"
                          >
                            <div class="activities-weekly-subtable__name-top">
                              <button
                                v-if="rescheduleMode"
                                type="button"
                                class="activities-weekly-subtable__pin-icon"
                                :class="{ 'activities-weekly-subtable__pin-icon--pending': isPendingRescheduling(sub) }"
                                :aria-label="isPendingRescheduling(sub) ? $t('activity_status_pending_rescheduling') : $t('reschedule')"
                                @click.stop="toggleSubactivityPendingPin(row, sub)"
                              >
                                <b-icon pack="fas" icon="thumbtack" size="is-small" />
                              </button>
                              <span
                                class="activities-weekly-subtable__name-text"
                                :class="{ 'activities-weekly-table__name-text--clickable': sub.access }"
                                @click="onSubactivityNameClicked(row, sub)"
                              >{{ sub.name }}</span>
                            </div>
                            <span
                              v-if="shouldShowSubactivityStatus(sub)"
                              class="activities-weekly-subtable__status"
                              :class="subactivityStatusClass(sub)"
                            >
                              <b-icon
                                v-if="isPendingRescheduling(sub)"
                                pack="fas"
                                icon="thumbtack"
                                size="is-small"
                                class="text-current"
                              />
                              {{ subactivityStatusLabel(sub) }}
                            </span>
                          </div>
                        </td>

                        <!-- Peso -->
                        <td v-if="isSubactivityColumnVisible('weight')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <span class="activities-weekly-table__weight">{{ sub.weight }}%</span>
                        </td>

                        <!-- Avance sem. ant. -->
                        <td v-if="isSubactivityColumnVisible('prev_week_progress')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <span v-if="isCellEmpty(sub.prev_week_progress)" class="activities-weekly-table__empty">—</span>
                          <span v-else>{{ sub.prev_week_progress }}%</span>
                        </td>

                        <!-- Progreso semanal -->
                        <td v-if="isSubactivityColumnVisible('weekly_progress')" class="activities-weekly-subtable__cell w-32">
                          <progress-bar
                            :step="parseFloat(sub.weekly_progress) || 0"
                            color="blue-light"
                            class="w-full"
                          />
                        </td>

                        <!-- Personas -->
                        <td v-if="isSubactivityColumnVisible('people')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <b-input
                            v-if="rescheduleMode"
                            v-model.number="sub.people"
                            type="number"
                            min="0"
                            size="is-small"
                            class="activities-weekly-subtable__inline-input"
                            @input="markSubactivityRescheduled(row, sub)"
                          />
                          <template v-else>
                            <span v-if="isCellEmpty(sub.people)" class="activities-weekly-table__empty">—</span>
                            <span v-else>{{ sub.people }}</span>
                          </template>
                        </td>

                        <!-- HH programadas -->
                        <td v-if="isSubactivityColumnVisible('scheduled_hours')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <span v-if="isCellEmpty(sub.scheduled_hours)" class="activities-weekly-table__empty">—</span>
                          <span v-else>{{ sub.scheduled_hours }}</span>
                        </td>

                        <!-- HH registradas -->
                        <td v-if="isSubactivityColumnVisible('registered_hours')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <span v-if="isCellEmpty(sub.registered_hours)" class="activities-weekly-table__empty">—</span>
                          <span v-else>{{ formatWeeklyManHours(sub.registered_hours) }}</span>
                        </td>

                        <!-- Inicio prog. -->
                        <td v-if="isSubactivityColumnVisible('current_start_date')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <b-datepicker
                            v-if="rescheduleMode"
                            v-model="sub._startDate"
                            size="is-small"
                            icon-pack="fas"
                            append-to-body
                            position="is-bottom-left"
                            :min-date="parentDateMin(row)"
                            :max-date="subStartMaxDate(row, sub)"
                            class="activities-weekly-subtable__inline-datepicker"
                            @input="onSubStartDateChange(row, sub, $event)"
                          />
                          <span v-else>{{ sub.current_start_date || '—' }}</span>
                        </td>

                        <!-- Fin prog. -->
                        <td v-if="isSubactivityColumnVisible('current_end_date')" class="activities-weekly-subtable__cell text-center whitespace-nowrap">
                          <b-datepicker
                            v-if="rescheduleMode"
                            v-model="sub._endDate"
                            size="is-small"
                            icon-pack="fas"
                            append-to-body
                            position="is-bottom-left"
                            :min-date="subEndMinDate(row, sub)"
                            :max-date="parentDateMax(row)"
                            class="activities-weekly-subtable__inline-datepicker"
                            @input="onSubEndDateChange(row, sub, $event)"
                          />
                          <span v-else>{{ sub.current_end_date || '—' }}</span>
                        </td>

                        <!-- Superv. -->
                        <td v-if="isSubactivityColumnVisible('supervisor')" class="activities-weekly-subtable__cell whitespace-nowrap">
                          {{ sub.supervisor || '—' }}
                        </td>

                        <!-- Encargado -->
                        <td v-if="isSubactivityColumnVisible('manager')" class="activities-weekly-subtable__cell activities-weekly-subtable__cell--manager whitespace-nowrap">
                          <b-autocomplete
                            v-if="rescheduleMode"
                            :ref="`managerAc_${sub.id}`"
                            v-model="sub.manager_search"
                            :data="sub.managerOptions || emptyManagerOptions"
                            field="fullname"
                            open-on-focus
                            clearable
                            append-to-body
                            size="is-small"
                            :placeholder="$t('select_manager')"
                            class="activities-weekly-subtable__inline-autocomplete"
                            @focus="onManagerFocus(sub)"
                            @typing="onManagerTyping(sub, $event)"
                            @select="onManagerSelected(sub, $event)"
                            @active="onManagerAutocompleteActive(sub, $event)"
                          >
                            <template v-slot="props">
                              <div class="text-xs">
                                <span class="font-semibold">{{ props.option.fullname }}</span>
                                <span v-if="props.option.email" class="text-grey-dark"> ({{ props.option.email }})</span>
                              </div>
                            </template>
                            <template #empty>{{ $t('no_results') }}</template>
                          </b-autocomplete>
                          <span v-else>{{ sub.manager || '—' }}</span>
                        </td>
                      </tr>
                      <tr
                        v-if="isSubactivityExpanded(row, sub, subIndex)"
                        class="activities-weekly-subtable__detail-row"
                      >
                        <td :colspan="subactivityTableColspan" class="p-0 bg-white">
                          <div class="activities-weekly-subtable__detail-panel activities-weekly-subtable__detail-panel--level-3">
                            <div
                              v-if="isFreeBoqRootMode"
                              class="activities-weekly-subtable__toolbar activities-weekly-subtable__toolbar--level-3"
                            >
                              <div class="flex items-center gap-2 flex-wrap">
                                <span class="activities-weekly-subtable__level-badge activities-weekly-subtable__level-badge--green">
                                  NIVEL 3
                                </span>
                                <button
                                  type="button"
                                  class="activities-weekly-subtable__add-btn activities-weekly-subtable__add-btn--green"
                                  @click="openCreateMeasurementForFreeMode(sub)"
                                >
                                  <b-icon pack="fas" icon="plus" size="is-small" />
                                  <span>{{ $t('create_measurement') }}</span>
                                </button>
                              </div>
                            </div>
                            <weekly-activity-assignments-table
                              :key="`sub-assignments-${sub.id}-${sub.assignments_num || 0}`"
                              :level="3"
                              :activity-id="sub.id"
                              :count="sub.assignments_num || 0"
                              count-label-key="assignments"
                              theme="green"
                              :show-toolbar="!isFreeBoqRootMode"
                              :activity-start-date="sub.current_start_date"
                              :activity-end-date="sub.current_end_date"
                              :activity-people-limit="sub.people"
                              @created="onSubactivityMeasurementCreated(row)"
                            />
                          </div>
                        </td>
                      </tr>
                      </tbody>
                  </table>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Mediciones (actividad padre: activity / takeoff / free sin subactividades) -->
                <template v-else-if="displayActivityAssignments(row)">
                  <weekly-activity-assignments-table-takeoff
                    v-if="isTakeoffProgressMode"
                    :key="`activity-assignments-takeoff-${row.id}-${row.assignments_num || 0}-${projectProgressTypeMode}-${selectedDisciplineId || 'all'}`"
                    :level="2"
                    :activity-id="row.id"
                    :project-id="effectiveProjectId"
                    :week-number="weekNumber"
                    :week-start-date="weekStartDate"
                    :week-end-date="weekEndDate"
                    :count="row.assignments_num || 0"
                    count-label-key="assignments"
                    :reschedule-mode="rescheduleMode"
                    :selected-discipline-id="selectedDisciplineId"
                  />
                  <weekly-activity-assignments-table
                    v-else
                    :key="`activity-assignments-${row.id}-${row.assignments_num || 0}-${projectProgressTypeMode}-${isFreeBoqRootMode ? 'free' : 'std'}`"
                    progress-type-mode="activity"
                    :level="2"
                    :activity-id="row.id"
                    :activity-name="row.name"
                    :project-id="effectiveProjectId"
                    :count="row.assignments_num || 0"
                    count-label-key="assignments"
                    :show-toolbar="!isFreeBoqRootMode"
                    :show-add-button="isActivityProgressMode"
                    :weight-edit-mode="isEditingMeasurements(row)"
                    @weight-edit-saved="onMeasurementsEditSaved(row, $event)"
                    :delete-mode="isDeletingMeasurements(row)"
                    :can-manage="!!row.is_project_manager"
                    @deleted="onMeasurementDeleted(row)"
                    :activity-start-date="row.current_start_date"
                    :activity-end-date="row.current_end_date"
                    :activity-people-limit="row.people"
                    @created="loadActivities"
                  />
                </template>
              </div>
            </td>
          </tr>
      </tbody>
    </table>
    </div>

    <div
      v-if="usesActivitiesPagination && activitiesTotal > activitiesPerPage"
      class="flex flex-wrap items-center justify-between gap-2 py-2 px-3 border-t"
      style="border-color: #c5d4eb;"
    >
      <span class="text-xs text-gray-600">{{ $t('count_results') }}: {{ activitiesTotal }}</span>
      <b-pagination
        :total="activitiesTotal"
        :current="activitiesPage"
        :per-page="activitiesPerPage"
        size="is-small"
        order="is-centered"
        pack="fas"
        icon-pack="fas"
        icon="chevron-left"
        icon-right="chevron-right"
        @change="onActivitiesPageChange"
      />
    </div>

    <weekly-admin-assignments-modal
      ref="weeklyAdminAssignmentsModal"
      @saved="onWeeklySubactivityAssignmentsSaved"
    />
    <weekly-linked-boq-roots-modal
      ref="weeklyLinkedBoqRootsModal"
      @primary-changed="onBoqPrimaryChanged"
    />
    <weekly-create-subactivity-modal
      ref="weeklyCreateSubactivityModal"
      :project-id="effectiveProjectId"
      :week-start-date="weekStartDate"
      :week-end-date="weekEndDate"
      :week-number="weekNumber"
      @created="loadActivities"
    />
    <edit-activity-modal
      ref="editActivityModal"
      :project-id="effectiveProjectId"
      @created="onEditActivityModalCreated"
      @updated="onEditActivityModalUpdated"
    />
    <weekly-edit-activity-modal
      ref="weeklyEditActivityModal"
      :project-id="effectiveProjectId"
      :reschedule-mode="rescheduleMode"
      @saved="onWeeklyActivityEdited"
    />
    <assignment-modal
      v-if="assignmentModalActivityId !== null"
      ref="assignment_modal"
      entity-type="activity"
      :entity-id="assignmentModalActivityId"
      @reload="onAssignmentModalReload"
    />
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import EventBus from '~/plugins/bus'
import WeeklyActivityAssignmentsTable from './weekly-activity-assignments-table.vue'
import WeeklyActivityAssignmentsTableTakeoff from './weekly-activity-assignments-table-takeoff.vue'
import WeeklyAdminAssignmentsModal from './weekly-admin-assignments-modal.vue'
import WeeklyCreateSubactivityModal from './weekly-create-subactivity-modal.vue'
import EditActivityModal from './edit-activity-modal.vue'
import WeeklyEditActivityModal from './weekly-edit-activity-modal.vue'
import WeeklyLinkedBoqRootsModal from './weekly-linked-boq-roots-modal.vue'
import { formatProjectFigure, quantityDisplayDecimals } from '·/helpers/quantity-display'
import assignmentModal from '·/components/assignment-modal'
import {
  mergeWeeklyUiSessionSection,
  readWeeklyUiSession,
  removeWeeklyUiSessionSubactivitiesForParent,
  writeWeeklyUiSession,
} from './weekly-ui-session.js'

const ACTIVITY_STATUS_PENDING_RESCHEDULING = 9
const ACTIVITY_STATUS_CLOSED = 6

const ACTIVITY_STATUS_LABEL_KEYS = [
  'activity_status_assigned',
  'activity_status_running',
  'activity_status_paused',
  'activity_status_issue',
  'activity_status_done',
  'activity_status_reopened',
  'activity_status_closed',
  'activity_status_delayed_start',
  'activity_status_delayed',
  'activity_status_pending_rescheduling',
]

const ACTIVITY_STATUS_CSS_SUFFIX = {
  0: 'assigned',
  1: 'running',
  2: 'paused',
  3: 'issue',
  4: 'done',
  5: 'reopened',
  6: 'closed',
  7: 'delayed-start',
  8: 'delayed',
  9: 'pending-rescheduling',
}

/** Modos de carga de actividades. El primero es el predeterminado. */
const ACTIVITIES_LOAD_OPTIONS = [
  {
    key: 'wbs_parents',
    buildRequest(projectId) {
      return {
        url: '/api/v2/activities',
        params: {
          p: projectId,
          pg: 1,
          include_parents_for_accessible_children: 1,
          order_field: 'planning_code',
          order_dir: 'asc',
          with_prelations: 1,
          with_fullpath: 1,
          s_num: 1,
        },
      }
    },
  },
  {
    key: 'group',
    buildRequest(projectId, groupId, options = {}) {
      if (!groupId) {
        return null
      }

      const params = {
        p: projectId,
        group_id: groupId,
        order_field: 'planning_code',
        order_dir: 'asc',
        with_prelations: 1,
        with_fullpath: 1,
        s_num: 1,
      }

      if (options.disciplineMode) {
        params.discipline_mode = 1
      }

      return {
        url: '/api/v2/activities',
        params,
      }
    },
  },
  {
    key: 'manager',
    buildRequest(projectId, groupId, options = {}) {
      if (!options.managerUserId) {
        return null
      }

      return {
        url: '/api/v2/activities',
        params: {
          p: projectId,
          manager_user_id: options.managerUserId,
          order_field: 'planning_code',
          order_dir: 'asc',
          with_prelations: 1,
          with_fullpath: 1,
          s_num: 1,
        },
      }
    },
  },
  {
    key: 'project',
    buildRequest(projectId, groupId, options = {}) {
      return {
        url: '/api/v2/activities',
        params: {
          p: projectId,
          include_parents_for_accessible_children: 1,
          order_field: 'planning_code',
          order_dir: 'asc',
          with_prelations: 1,
          with_fullpath: 1,
          with_location: 1,
          per_page: options.perPage || 25,
          page: options.page || 1,
        },
      }
    },
  },
]

// Mismo formato que Activity::nextAutoPlanningCode (EPCT_0001...)
const AUTO_PLANNING_CODE_REGEX = /^EPCT_\d+$/

/** Las actividades creadas desde el modal (no importadas) tienen planning_code autogenerado. */
function isManuallyCreatedCode(code) {
  return typeof code === 'string' && AUTO_PLANNING_CODE_REGEX.test(code)
}

function prelationCodes(activities) {
  if (!Array.isArray(activities) || !activities.length) {
    return []
  }

  return activities.map((item) => item.planning_code)
    .filter((code) => code != null && String(code).trim() !== '')
}

function parseApiDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.toDate() : null
}

function formatApiDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null
}

/** Misma lógica que ActivityService::getActivityDurationInDays (abs(diffInDays) + 1). */
function resolveDurationDays(startDate, endDate) {
  if (!startDate || !endDate) {
    return null
  }

  const start = moment(startDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  const end = moment(endDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  if (!start.isValid() || !end.isValid()) {
    return null
  }

  return Math.abs(end.diff(start, 'days')) + 1
}

function buildManagerLabel(manager) {
  if (!manager) {
    return null
  }
  if (manager.fullname) {
    return manager.fullname
  }
  const parts = [manager.name, manager.surname].filter(Boolean)
  return parts.length ? parts.join(' ') : null
}

/**
 * Búsqueda de encargado: misma API que userlist en modo creación
 * (GET /api/v2/activity/users/search, sin activity id en la URL).
 */
function buildManagerSearchRequest(projectId, query) {
  return {
    url: '/api/v2/activity/users/search',
    params: {
      only_internal: 1,
      p: projectId,
      q: query,
    },
  }
}

function mapManagerSearchResults(users) {
  if (!Array.isArray(users)) {
    return []
  }
  return users.map((user) => ({
    ...user,
    fullname: buildManagerLabel(user) || `${user.name || ''} ${user.surname || ''}`.trim(),
  }))
}

function resolveApiIsPrimary(value) {
  return value === true || value === 1 || value === '1'
}

function resolvePrimaryBoqRoot(boqRoots) {
  if (!Array.isArray(boqRoots) || boqRoots.length === 0) {
    return null
  }

  if (boqRoots.length === 1) {
    return boqRoots[0]
  }

  return boqRoots.find((root) => resolveApiIsPrimary(root.is_primary)) || null
}

function resolvePrimaryBoqRootQuantity(boqRoots) {
  const primary = resolvePrimaryBoqRoot(boqRoots)
  if (!primary) {
    return null
  }

  const qty = primary.quantity
  return qty != null && qty !== '' ? qty : null
}

function resolvePrimaryBoqRootUnit(boqRoots) {
  const primary = resolvePrimaryBoqRoot(boqRoots)
  if (!primary || !primary.measure_unit) {
    return null
  }

  return primary.measure_unit.code || primary.measure_unit.name || null
}

function resolvePendingManHours(scheduled, done) {
  if (scheduled == null || scheduled === '') {
    return null
  }

  const scheduledNum = parseFloat(scheduled)
  if (Number.isNaN(scheduledNum)) {
    return null
  }

  let doneNum = 0
  if (done != null && done !== '') {
    doneNum = parseFloat(done)
    if (Number.isNaN(doneNum)) {
      doneNum = 0
    }
  }

  return Math.max(0, Math.round((scheduledNum - doneNum) * 100000) / 100000)
}

/** Progreso temporal: línea base (scheduled) y última planificación (current), igual que activities.vue. */
function syncPlanningProgressFields(row, calculateProgress) {
  if (typeof calculateProgress !== 'function') {
    return row
  }

  row.baseline_line = calculateProgress(row.scheduled_start_date, row.scheduled_end_date)
  row.latest_planning = calculateProgress(row.current_start_date, row.current_end_date)
  row.planned_progress = resolveAccumulatedPhysicalProgress(row.physical_quantity, row.baseline_line)
  row.scheduled_progress = resolveAccumulatedPhysicalProgress(row.physical_quantity, row.latest_planning)
  return row
}

/** Avance acumulado en unidades BOQ primarias = cantidad física × progreso temporal (%). */
function resolveAccumulatedPhysicalProgress(physicalQuantity, progressPercent) {
  const quantity = parseFloat(physicalQuantity)
  const progress = parseFloat(progressPercent)
  if (Number.isNaN(quantity) || quantity <= 0 || Number.isNaN(progress)) {
    return null
  }

  return Math.round((quantity * progress / 100) * 100) / 100
}

/** Progreso importado de Primavera: prioriza p6_activity_progress; si no hay dato, p6_physical_progress. */
function resolveP6Progress(activity) {
  if (!activity) {
    return null
  }

  const activityProgress = activity.p6_activity_progress
  if (activityProgress != null && activityProgress !== '') {
    const value = parseFloat(activityProgress)
    if (!Number.isNaN(value)) {
      return value
    }
  }

  const physicalProgress = activity.p6_physical_progress
  if (physicalProgress != null && physicalProgress !== '') {
    const value = parseFloat(physicalProgress)
    if (!Number.isNaN(value)) {
      return value
    }
  }

  return null
}

/** KPI = cantidad física primaria / días de duración del grupo. */
function resolveActivityKpi(physicalQuantity, durationDays) {
  const quantity = parseFloat(physicalQuantity)
  const days = parseFloat(durationDays)
  if (Number.isNaN(quantity) || quantity <= 0 || Number.isNaN(days) || days <= 0) {
    return null
  }

  return Math.round((quantity / days) * 100) / 100
}

function syncActivityKpiFields(row) {
  if (!row) {
    return row
  }

  row.planned_kpi = resolveActivityKpi(row.physical_quantity, row.scheduled_duration_days)
  row.scheduled_kpi = resolveActivityKpi(row.physical_quantity, row.current_duration_days)
  row.real_kpi = resolveActivityKpi(row.physical_quantity, row.real_duration_days)
  return row
}

/** Mapea una actividad de GET /api/v2/activities al formato de la tabla. */
function mapActivityToWeeklyRow(activity, options = {}) {
  const { canProgress = false, calculateProgress = null } = options
  const managerLabel = buildManagerLabel(activity.manager)
  const currentStartDate = activity.current_start_date || null
  const currentEndDate = activity.current_end_date || null
  const scheduledStartDate = activity.scheduled_start_date || null
  const scheduledEndDate = activity.scheduled_end_date || null
  const realStartDate = activity.gantt_real_start_date || null
  const realEndDate = activity.gantt_real_end_date || null
  const trackingScheduledHours = activity.p6_budgeted_mh != null ? activity.p6_budgeted_mh : null
  const trackingDoneHours = activity.registered_man_hours != null ? activity.registered_man_hours : null

  const row = {
    id: activity.id,
    code: activity.planning_code || null,
    created_at: activity.created_at || null,
    is_subactivity: !!activity.is_subactivity,
    parent_id: activity.parent_id != null ? activity.parent_id : activity.id,
    without_access: activity.without_access === 1 ? 1 : 0,
    access: activity.access !== false,
    is_creator: !!activity.is_creator,
    is_manager: !!activity.is_manager,
    is_project_manager: !!activity.is_project_manager,
    kks: null,
    group: activity.group || null,
    full_path: activity.full_path || null,
    name: activity.name || null,
    status: activity.status != null ? activity.status : null,
    current_start_date: currentStartDate,
    current_end_date: currentEndDate,
    subactivities_num: activity.subactivities_num != null ? activity.subactivities_num : 0,
    assignments_num: activity.assignments_num != null ? activity.assignments_num : 0,
    measurements_count: activity.assignments_num != null ? activity.assignments_num : null,
    predecessors: prelationCodes(activity.pre_activities),
    successors: prelationCodes(activity.next_activities),
    baseline_line: null,
    latest_planning: null,
    progress: activity.progress != null ? activity.progress : null,
    last_assignment_date: activity.last_assignment_date || null,
    p6_progress: resolveP6Progress(activity),
    p6_budgeted_mh: activity.p6_budgeted_mh != null ? activity.p6_budgeted_mh : null,
    weight: activity.weight != null ? activity.weight : null,
    people: activity.people != null ? activity.people : null,
    physical_quantity: resolvePrimaryBoqRootQuantity(activity.boq_roots),
    tracking_done: activity.boq_roots_realized != null ? activity.boq_roots_realized : null,
    tracking_pending: activity.boq_roots_pending != null ? activity.boq_roots_pending : null,
    tracking_scheduled_hours: trackingScheduledHours,
    tracking_done_hours: trackingDoneHours,
    tracking_pending_hours: resolvePendingManHours(trackingScheduledHours, trackingDoneHours),
    scheduled_start_date: scheduledStartDate,
    scheduled_end_date: scheduledEndDate,
    scheduled_duration_days: resolveDurationDays(scheduledStartDate, scheduledEndDate),
    planned_float: null,
    planned_progress: null,
    planned_kpi: null,
    current_duration_days: resolveDurationDays(currentStartDate, currentEndDate),
    scheduled_float: null,
    scheduled_progress: null,
    scheduled_kpi: null,
    real_start: realStartDate,
    real_end: realEndDate,
    real_duration_days: resolveDurationDays(realStartDate, realEndDate),
    real_float: null,
    real_kpi: null,
    supervisor: null,
    manager_id: activity.manager && activity.manager.id != null ? activity.manager.id : null,
    manager: managerLabel,
    manager_search: managerLabel || '',
    managerOptions: [],
    _startDate: parseApiDate(currentStartDate),
    _endDate: parseApiDate(currentEndDate),
    boq_roots: Array.isArray(activity.boq_roots) ? activity.boq_roots : [],
    primary_boq_unit: resolvePrimaryBoqRootUnit(activity.boq_roots),
  }

  if (canProgress) {
    syncPlanningProgressFields(row, calculateProgress)
  }

  syncActivityKpiFields(row)

  return row
}

/**
 * Mapea una subactividad del API al formato de la tabla NIVEL 2.
 */
/** Guarda los valores originales de la subactividad (solo una vez, al cargar). */
function initRescheduleBaseline(sub) {
  if (sub._rescheduleBaseline) {
    return sub
  }

  const peopleRaw = sub.people
  const people = peopleRaw != null && peopleRaw !== '' && !Number.isNaN(parseInt(peopleRaw, 10))
    ? parseInt(peopleRaw, 10)
    : null

  sub._rescheduleBaseline = {
    current_start_date: sub.current_start_date,
    current_end_date: sub.current_end_date,
    people,
    manager_id: sub.manager_id != null ? sub.manager_id : null,
    status: sub.status,
  }
  return sub
}

function applyRescheduledEntry(sub, entry) {
  initRescheduleBaseline(sub)

  if (!entry) {
    return sub
  }

  sub.current_start_date = entry.current_start_date
  sub.current_end_date = entry.current_end_date
  sub.current_duration_days = resolveDurationDays(entry.current_start_date, entry.current_end_date)
  sub.people = entry.people
  sub.manager_id = entry.manager_user_id
  sub.status = entry.status != null ? entry.status : ACTIVITY_STATUS_PENDING_RESCHEDULING
  sub._startDate = parseApiDate(entry.current_start_date)
  sub._endDate = parseApiDate(entry.current_end_date)

  return sub
}

function mapSubactivityToWeeklyRow(subactivity, parentActivityRow = null) {
  const managerLabel = buildManagerLabel(subactivity.manager)
  const supervisor = subactivity.manager && subactivity.manager.department
    ? subactivity.manager.department
    : null
  const currentStartDate = subactivity.current_start_date || subactivity.scheduled_start_date || null
  const currentEndDate = subactivity.current_end_date || subactivity.scheduled_end_date || null
  const parentP6BudgetedMh = parentActivityRow && parentActivityRow.p6_budgeted_mh != null
    ? parentActivityRow.p6_budgeted_mh
    : null

  return {
    id: subactivity.id,
    code: subactivity.planning_code || null,
    created_at: subactivity.created_at || null,
    is_subactivity: true,
    parent_id: subactivity.parent_id,
    access: subactivity.access !== false,
    is_creator: !!subactivity.is_creator,
    is_manager: !!subactivity.is_manager,
    is_project_manager: !!subactivity.is_project_manager,
    name: subactivity.name || null,
    status: subactivity.status != null ? subactivity.status : null,
    weight: subactivity.weight != null ? subactivity.weight : null,
    assignments_num: subactivity.assignments_num != null ? subactivity.assignments_num : 0,
    prev_week_progress: subactivity.prev_week_progress != null ? subactivity.prev_week_progress : null,
    weekly_progress: subactivity.progress != null ? subactivity.progress : null,
    people: subactivity.people != null ? subactivity.people : null,
    scheduled_hours: parentP6BudgetedMh,
    registered_hours: subactivity.registered_man_hours != null ? subactivity.registered_man_hours : null,
    current_start_date: currentStartDate,
    current_end_date: currentEndDate,
    _startDate: parseApiDate(currentStartDate),
    _endDate: parseApiDate(currentEndDate),
    supervisor,
    manager_id: subactivity.manager && subactivity.manager.id != null ? subactivity.manager.id : null,
    manager: managerLabel,
    manager_search: managerLabel || '',
    managerOptions: [],
  }
}

/** Columnas de la subtabla NIVEL 2 (sin expand ni acciones). */
const SUBACTIVITY_TABLE_COLUMNS = [
  { key: 'code', labelKey: 'code', align: 'left' },
  { key: 'name', labelKey: 'name', align: 'left' },
  { key: 'weight', labelKey: 'weight', align: 'center' },
  { key: 'prev_week_progress', labelKey: 'prev_week_progress', align: 'center' },
  { key: 'weekly_progress', labelKey: 'weekly_progress', align: 'center' },
  { key: 'people', labelKey: 'people', align: 'center' },
  { key: 'scheduled_hours', labelKey: 'scheduled_hours', align: 'center' },
  { key: 'registered_hours', labelKey: 'registered_hours', align: 'center' },
  { key: 'current_start_date', labelKey: 'scheduled_start_date', align: 'center' },
  { key: 'current_end_date', labelKey: 'scheduled_end_date', align: 'center' },
  { key: 'supervisor', labelKey: 'supervisor', align: 'center' },
  { key: 'manager', labelKey: 'person_in_charge', align: 'center' },
]

/** Máximo de códigos de predecesoras/sucesoras visibles antes de «Ver más». */
const RELATION_CODES_PREVIEW = 5

/** Orden y títulos de columnas (fila 2 de cabecera). La columna expand va aparte. */
const WEEKLY_TABLE_COLUMNS = [
  { key: 'code', label: 'Código', group: 'identification' },
  { key: 'kks', label: 'KKS', group: 'identification' },
  { key: 'group', label: '', group: 'identification' },
  { key: 'name', label: 'Nombre', group: 'identification' },
  { key: 'predecessors', label: 'Predecesoras', group: 'tracking' },
  { key: 'successors', label: 'Sucesoras', group: 'tracking' },
  { key: 'baseline_line', label: 'Línea Base', group: 'tracking' },
  { key: 'latest_planning', label: 'Última planificación', group: 'tracking' },
  { key: 'progress', label: 'Progreso', group: 'tracking' },
  { key: 'last_assignment_date', labelKey: 'last_assignment_update', group: 'tracking', freeModeOnly: true },
  { key: 'p6_progress', labelKey: 'p6_progress', group: 'tracking' },
  { key: 'weight', label: 'Peso', group: 'tracking' },
  { key: 'physical_quantity', label: 'Cantidad física', group: 'tracking' },
  { key: 'tracking_done', label: 'Realizado', group: 'tracking' },
  { key: 'tracking_pending', label: 'Pendiente', group: 'tracking' },
  { key: 'tracking_scheduled_hours', label: 'HH programadas', group: 'tracking' },
  { key: 'tracking_done_hours', label: 'HH realizadas', group: 'tracking' },
  { key: 'tracking_pending_hours', label: 'HH pendientes', group: 'tracking' },
  { key: 'supervisor', labelKey: 'supervisor', group: 'tracking' },
  { key: 'manager', labelKey: 'person_in_charge', group: 'tracking' },
  { key: 'people', labelKey: 'people', group: 'planned_advance' },
  { key: 'scheduled_start_date', label: 'Inicio planificado', group: 'planned_advance' },
  { key: 'scheduled_end_date', label: 'Fin planificado', group: 'planned_advance' },
  { key: 'scheduled_duration_days', label: 'Duración plan.', group: 'planned_advance' },
  { key: 'planned_float', label: 'Float plan.', group: 'planned_advance' },
  { key: 'planned_progress', label: 'Avance acum. plan.', group: 'planned_advance' },
  { key: 'planned_kpi', label: 'KPI', group: 'planned_advance' },
  { key: 'current_start_date', label: 'Inicio programado', group: 'scheduled_advance' },
  { key: 'current_end_date', label: 'Fin programado', group: 'scheduled_advance' },
  { key: 'current_duration_days', label: 'Duración prog.', group: 'scheduled_advance' },
  { key: 'scheduled_float', label: 'Float prog.', group: 'scheduled_advance' },
  { key: 'scheduled_progress', label: 'Avance acum. prog.', group: 'scheduled_advance' },
  { key: 'scheduled_kpi', label: 'KPI', group: 'scheduled_advance' },
  { key: 'real_start', label: 'Inicio real', group: 'real' },
  { key: 'real_end', label: 'Fin real', group: 'real' },
  { key: 'real_duration_days', label: 'Duración real', group: 'real' },
  { key: 'real_float', label: 'Float real', group: 'real' },
  { key: 'real_kpi', label: 'KPI', group: 'real' },
]

const WEEKLY_TABLE_GROUP_META = {
  identification: {
    key: 'identification',
    labelKey: 'activities',
    headerClass: 'activities-weekly-table__group--identification',
    colClass: 'activities-weekly-table__col--identification',
  },
  tracking: {
    key: 'tracking',
    label: 'Información de seguimiento',
    headerClass: 'activities-weekly-table__group--tracking',
    colClass: 'activities-weekly-table__col--tracking',
  },
  planned_advance: {
    key: 'planned_advance',
    labelKey: 'planned_advance',
    headerClass: 'activities-weekly-table__group--planned-advance',
    colClass: 'activities-weekly-table__col--planned-advance',
  },
  scheduled_advance: {
    key: 'scheduled_advance',
    labelKey: 'scheduled_advance',
    headerClass: 'activities-weekly-table__group--scheduled-advance',
    colClass: 'activities-weekly-table__col--scheduled-advance',
  },
  real: {
    key: 'real',
    label: 'Avance real',
    headerClass: 'activities-weekly-table__group--real',
    colClass: 'activities-weekly-table__col--real',
  },
}

/**
 * @param {boolean} includeActivityModeColumns
 * @param {Function|null} isColumnVisible columnas ocultas por configs.weekly_columns del proyecto
 * @param {boolean} includeFreeModeColumns columnas exclusivas del modo free (configs.boq_root === 'free')
 */
function buildColumnGroups(includeActivityModeColumns = false, isColumnVisible = null, includeFreeModeColumns = false) {
  const groups = []
  let currentGroupKey = null
  let currentGroup = null

  WEEKLY_TABLE_COLUMNS.forEach((col) => {
    if (col.activityModeOnly && !includeActivityModeColumns) {
      return
    }
    if (col.freeModeOnly && !includeFreeModeColumns) {
      return
    }
    // Identificación siempre existe: su cabecera agrupa también las columnas expand y acciones
    if (isColumnVisible && !isColumnVisible(col.key)) {
      if (col.group === 'identification' && !groups.some((group) => group.key === 'identification')) {
        const meta = WEEKLY_TABLE_GROUP_META.identification
        currentGroupKey = col.group
        currentGroup = {
          key: meta.key,
          label: meta.label || null,
          labelKey: meta.labelKey || null,
          headerClass: meta.headerClass,
          columns: [],
        }
        groups.push(currentGroup)
      }
      return
    }
    if (col.group !== currentGroupKey) {
      currentGroupKey = col.group
      const meta = WEEKLY_TABLE_GROUP_META[col.group]
      currentGroup = {
        key: meta.key,
        label: meta.label || null,
        labelKey: meta.labelKey || null,
        headerClass: meta.headerClass,
        columns: [],
      }
      groups.push(currentGroup)
    }
    currentGroup.columns.push({
      key: col.key,
      label: col.label,
      labelKey: col.labelKey || null,
      headerClass: WEEKLY_TABLE_GROUP_META[col.group].colClass,
    })
  })

  return groups
}

export default {
  name: 'activities-weekly-table',

  components: {
    WeeklyActivityAssignmentsTable,
    WeeklyActivityAssignmentsTableTakeoff,
    WeeklyAdminAssignmentsModal,
    WeeklyCreateSubactivityModal,
    EditActivityModal,
    WeeklyEditActivityModal,
    WeeklyLinkedBoqRootsModal,
    assignmentModal,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    /** Define qué actividades cargar desde el API. */
    activitiesLoadMode: {
      type: String,
      default() {
        return ACTIVITIES_LOAD_OPTIONS[0].key
      },
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    groupId: {
      type: Number,
      default: null,
    },
    /** Con group_id, carga actividades de la disciplina y todos sus subgrupos. */
    disciplineMode: {
      type: Boolean,
      default: false,
    },
    /** Usuario responsable (activity_admin) para el modo manager. */
    managerUserId: {
      type: Number,
      default: null,
    },
    /** Oculta barra resumen y chips de columnas (p. ej. dentro de un grupo WBS). */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** Grupos de columnas ocultos; si se pasa, la visibilidad la controla el padre. */
    hiddenColumnGroups: {
      type: Array,
      default: null,
    },
    /** Si true, las subactividades se cargan filtradas por semana (weekly_start/end). */
    filterSubactivitiesByWeek: {
      type: Boolean,
      default: true,
    },
    subactivitiesCount: {
      type: Number,
      default: 1002,
    },
    /** Modo reprogramación: campos editables en subactividades. */
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    activityFilters: {
      type: Object,
      default: () => ({}),
    },
    /** Disciplina BoqRoot (null = Todas). Takeoff: filtra por stages del subárbol. */
    selectedDisciplineId: {
      type: Number,
      default: null,
    },
    /** Etiqueta del badge en la barra resumen (clave i18n). Por defecto WBS. */
    summaryBadgeKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      activityRows: [],
      visibleTableWidth: null,
      /** Actividades (modo free) con sus mediciones en edición de ponderación / unidades. */
      editingMeasurementsActivityIds: [],
      /** Actividades (modo free) con sus mediciones en modo eliminación. */
      deletingMeasurementsActivityIds: [],
      activitiesPage: 1,
      activitiesPerPage: 25,
      activitiesTotal: 0,
      isLoadingActivities: false,
      expandedRowKeys: [],
      expandedSubactivityKeys: [],
      /** Claves `rowId:predecessors|successors` con la lista de relaciones expandida. */
      expandedRelationKeys: [],
      localHiddenColumnGroups: [],
      /** Subactividades cargadas por actividad padre (activityId → filas). */
      subactivitiesByParentId: {},
      /** Indicador de carga por actividad padre. */
      subactivitiesLoading: {},
      notificationsRefreshTimer: null,
      /** Array vacío compartido para b-autocomplete (evita referencias nuevas en cada render). */
      emptyManagerOptions: [],
      /** Actividad para la que se abre assignment-modal; null = modal no montado */
      assignmentModalActivityId: null,
      groupFilters: [
        { key: 'info', columnGroup: 'tracking', labelKey: 'information', color: '#3b82f6', count: 13 },
        { key: 'planned_advance', columnGroup: 'planned_advance', labelKey: 'planned_advance', color: '#1e3a5f', count: 6 },
        { key: 'scheduled_advance', columnGroup: 'scheduled_advance', labelKey: 'scheduled_advance', color: '#166534', count: 6 },
        { key: 'real', columnGroup: 'real', labelKey: 'real_progress', color: '#7c3aed', count: 5 },
      ],
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    displayDecimals() {
      return quantityDisplayDecimals(this.project)
    },

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    activitiesCount() {
      if (this.usesActivitiesPagination) {
        return this.activitiesTotal
      }
      return this.activityRows.length
    },

    summaryBadgeLabel() {
      if (this.summaryBadgeKey) {
        return this.$t(this.summaryBadgeKey)
      }
      return 'WBS'
    },

    usesActivitiesPagination() {
      return this.activitiesLoadMode === 'project'
    },

    summaryCounts() {
      return this.$t('activities_subactivities_count', {
        activities: this.activitiesCount,
        subactivities: this.subactivitiesCount,
      })
    },

    weekSummaryLabel() {
      if (!this.weekStartDate || !this.weekEndDate || this.weekNumber == null) {
        return ''
      }

      const start = moment(this.weekStartDate, 'YYYY-MM-DD', true)
      const end = moment(this.weekEndDate, 'YYYY-MM-DD', true)
      if (!start.isValid() || !end.isValid()) {
        return ''
      }

      const weekNum = String(this.weekNumber).padStart(2, '0')
      const rangeStart = start.format('DD/MM/YYYY')
      const rangeEnd = end.format('DD/MM/YYYY')
      const range = rangeStart === rangeEnd
        ? rangeStart
        : `${rangeStart} – ${rangeEnd}`

      return `Semana S${weekNum} · ${range}`
    },

    visibleColumnGroups() {
      return this.effectiveColumnGroups.filter((group) => this.isGroupVisible(group.key))
    },

    effectiveColumnGroups() {
      return buildColumnGroups(this.isActivityLikeProgressMode, this.isActivityColumnVisible, this.isFreeBoqRootMode)
    },

    /** Configuración visual del proyecto (visual-config: columnas de la vista semanal). */
    weeklyColumnsConfig() {
      const configs = this.project && this.project.configs ? this.project.configs : null
      return configs && configs.weekly_columns ? configs.weekly_columns : {}
    },

    groupFiltersWithCounts() {
      return this.groupFilters
        .map((group) => {
          const columnGroup = this.effectiveColumnGroups.find((item) => item.key === group.columnGroup)
          return columnGroup
            ? { ...group, count: columnGroup.columns.length }
            : group
        })
        // Grupos sin columnas visibles por la configuración del proyecto
        .filter((group) => group.count !== 0 && this.effectiveColumnGroups.some((item) => item.key === group.columnGroup))
    },

    visibleFlatColumns() {
      return this.visibleColumnGroups.reduce((columns, group) => {
        return columns.concat(group.columns)
      }, [])
    },

    rootStyle() {
      const style = this.embedded ? {} : { borderColor: '#5b80be' }
      if (this.visibleTableWidth) {
        style['--weekly-visible-width'] = `${this.visibleTableWidth}px`
      }
      return style
    },

    totalColumnCount() {
      return this.visibleFlatColumns.length + 1 + (this.showActivityActionsColumn ? 1 : 0)
    },

    /** Por ahora las acciones de actividad (editar en modal) solo existen en modo free. */
    showActivityActionsColumn() {
      return this.isFreeBoqRootMode
    },

    /** Clave de la semana activa para invalidar caché de subactividades al cambiar de semana. */
    weeklyDatesKey() {
      if (!this.weekStartDate || !this.weekEndDate) {
        return null
      }
      return `${this.weekStartDate}_${this.weekEndDate}`
    },

    effectiveHiddenColumnGroups() {
      return this.hiddenColumnGroups != null
        ? this.hiddenColumnGroups
        : this.localHiddenColumnGroups
    },

    subactivityTableColumns() {
      return SUBACTIVITY_TABLE_COLUMNS.filter((column) => this.isSubactivityColumnVisible(column.key))
    },

    /** Expand + columnas de datos visibles + acciones. */
    subactivityTableColspan() {
      return this.subactivityTableColumns.length + 2
    },

    /** Proyecto en modo planificación semanal (configs.progress_type). */
    isWeeklyWorkMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type === 'weekly')
    },

    /** Modo libre: mediciones en actividad o subactividad sin fijar un único nivel. */
    isFreeBoqRootMode() {
      if (!this.isWeeklyWorkMode) {
        return false
      }
      const configs = this.project && this.project.configs
      return !!(configs && configs.boq_root === 'free')
    },

    canProgress() {
      // return !!(this.project && this.project.can_progress)
      // temporal luego verificamos si sera necesario validar si el usuario puede progresar
      return true
    },

    /** 'activity' | 'subactivity' | 'takeoff' | null según configs del proyecto. */
    projectProgressTypeMode() {
      if (!this.isWeeklyWorkMode) {
        return null
      }
      const configs = this.project.configs
      return configs.progress_type_mode || null
    },

    isSubactivityProgressMode() {
      return this.projectProgressTypeMode === 'subactivity'
    },

    isActivityProgressMode() {
      return this.projectProgressTypeMode === 'activity'
    },

    isTakeoffProgressMode() {
      return this.projectProgressTypeMode === 'takeoff'
    },

    /** Modos que muestran mediciones en la actividad padre (sin subactividades). */
    isActivityLikeProgressMode() {
      return this.isActivityProgressMode || this.isTakeoffProgressMode
    },

    isActivityRescheduleMode() {
      return this.isActivityLikeProgressMode && this.rescheduleMode
    },

    usesWeeklyActivityFilter() {
      return this.isActivityLikeProgressMode && this.filterSubactivitiesByWeek
    },

    /**
     * Fin del rango semanal enviado a la API.
     * En takeoff + vista semanal: +35 días (ventana de 6 semanas desde la semana actual).
     */
    weeklyApiEndDate() {
      if (!this.weekEndDate) {
        return null
      }
      if (!this.isTakeoffProgressMode) {
        return this.weekEndDate
      }
      const end = moment(this.weekEndDate, 'YYYY-MM-DD', true)
      if (!end.isValid()) {
        return this.weekEndDate
      }
      return end.clone().add(35, 'days').format('YYYY-MM-DD')
    },
  },

  watch: {
    effectiveProjectId(projectId, previousProjectId) {
      if (projectId && projectId !== previousProjectId) {
        this.resetActivitiesPagination()
        this.loadActivities()
      }
    },
    activitiesLoadMode() {
      this.resetActivitiesPagination()
      this.loadActivities()
    },
    groupId(groupId, previousGroupId) {
      if (groupId !== previousGroupId) {
        this.loadActivities()
      }
    },
    disciplineMode() {
      this.loadActivities()
    },
    managerUserId(managerUserId, previousManagerUserId) {
      if (managerUserId !== previousManagerUserId) {
        this.loadActivities()
      }
    },
    weeklyDatesKey(newKey, oldKey) {
      if (newKey === oldKey) {
        return
      }
      if (!this.filterSubactivitiesByWeek) {
        return
      }
      if (this.isActivityLikeProgressMode) {
        this.resetActivitiesPagination()
        this.loadActivities()
        return
      }
      this.subactivitiesByParentId = {}
      this.expandedSubactivityKeys = []
      this.persistActivitiesSession()
      this.reloadSubactivitiesForExpandedParents()
    },
    filterSubactivitiesByWeek() {
      if (this.isActivityLikeProgressMode) {
        this.resetActivitiesPagination()
        this.loadActivities()
        return
      }
      this.subactivitiesByParentId = {}
      this.expandedSubactivityKeys = []
      this.persistActivitiesSession()
      this.reloadSubactivitiesForExpandedParents()
    },
    selectedDisciplineId() {
      if (!this.isTakeoffProgressMode) {
        return
      }
      this.resetActivitiesPagination()
      this.loadActivities()
    },
    activityFilters: {
      deep: true,
      handler() {
        this.resetActivitiesPagination()
        this.subactivitiesByParentId = {}
        this.expandedSubactivityKeys = []
        this.loadActivities()
      },
    },
    rescheduleMode(active) {
      if (this.isActivityLikeProgressMode) {
        if (!active) {
          this.loadActivities()
        } else {
          this.applyEditedActivitiesToRows()
        }
        return
      }
      if (!active) {
        this.subactivitiesByParentId = {}
        this.expandedSubactivityKeys = []
        this.persistActivitiesSession()
        this.reloadSubactivitiesForExpandedParents()
      }
    },
    editedSubactivities: {
      deep: true,
      handler() {
        this.applyEditedSubactivitiesToLoadedSubactivities()
        this.applyEditedActivitiesToRows()
      },
    },
  },

  created() {
    this.loadActivities()
    this.registerNotificationsRefreshListeners()
  },

  mounted() {
    this.observeVisibleWidth()
  },

  beforeDestroy() {
    this.unregisterNotificationsRefreshListeners()
    this.unobserveVisibleWidth()
  },

  methods: {

    formatWeeklyManHours(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return value
      }
      return String(Math.round(number * 100) / 100)
    },

    /** Sin configuración guardada todas las columnas son visibles. */
    isWeeklyColumnVisible(level, key) {
      const levelConfig = this.weeklyColumnsConfig[level]
      const column = levelConfig ? levelConfig[key] : null
      return !(column && column.visible === false)
    },

    isActivityColumnVisible(key) {
      return this.isWeeklyColumnVisible('activity', key)
    },

    isSubactivityColumnVisible(key) {
      return this.isWeeklyColumnVisible('subactivity', key)
    },

    /** Ancho visible de la tabla padre: mínimo que ocupan los paneles hijos (nivel 2). */
    observeVisibleWidth() {
      const update = () => {
        this.visibleTableWidth = this.$el && this.$el.clientWidth ? this.$el.clientWidth : null
      }
      update()
      if (typeof ResizeObserver === 'undefined' || !this.$el) {
        this._onWindowResizeVisibleWidth = update
        window.addEventListener('resize', update)
        return
      }
      this._visibleWidthObserver = new ResizeObserver(update)
      this._visibleWidthObserver.observe(this.$el)
    },

    unobserveVisibleWidth() {
      if (this._visibleWidthObserver) {
        this._visibleWidthObserver.disconnect()
        this._visibleWidthObserver = null
      }
      if (this._onWindowResizeVisibleWidth) {
        window.removeEventListener('resize', this._onWindowResizeVisibleWidth)
        this._onWindowResizeVisibleWidth = null
      }
    },

    registerNotificationsRefreshListeners() {
      const scheduleSilentRefresh = () => this.scheduleNotificationsSilentRefresh()
      this._onRefreshActivitiesNotifications = scheduleSilentRefresh
      this._onRefreshSubactivitiesNotifications = scheduleSilentRefresh
      this._onRefreshAssignmentChangedNotifications = scheduleSilentRefresh
      this._onRefreshAssignmentCreatedNotifications = scheduleSilentRefresh
      this._onRefreshAssignmentDeletedNotifications = scheduleSilentRefresh

      EventBus.$on('refreshActivitiesNotifications', this._onRefreshActivitiesNotifications)
      EventBus.$on('refreshSubactivitiesNotifications', this._onRefreshSubactivitiesNotifications)
      EventBus.$on('refreshAssignmentChangedNotifications', this._onRefreshAssignmentChangedNotifications)
      EventBus.$on('refreshAssignmentCreatedNotifications', this._onRefreshAssignmentCreatedNotifications)
      EventBus.$on('refreshAssignmentDeletedNotifications', this._onRefreshAssignmentDeletedNotifications)
    },

    unregisterNotificationsRefreshListeners() {
      if (this.notificationsRefreshTimer) {
        clearTimeout(this.notificationsRefreshTimer)
        this.notificationsRefreshTimer = null
      }
      EventBus.$off('refreshActivitiesNotifications', this._onRefreshActivitiesNotifications)
      EventBus.$off('refreshSubactivitiesNotifications', this._onRefreshSubactivitiesNotifications)
      EventBus.$off('refreshAssignmentChangedNotifications', this._onRefreshAssignmentChangedNotifications)
      EventBus.$off('refreshAssignmentCreatedNotifications', this._onRefreshAssignmentCreatedNotifications)
      EventBus.$off('refreshAssignmentDeletedNotifications', this._onRefreshAssignmentDeletedNotifications)
    },

    scheduleNotificationsSilentRefresh() {
      if (this.notificationsRefreshTimer) {
        clearTimeout(this.notificationsRefreshTimer)
      }
      this.notificationsRefreshTimer = setTimeout(() => {
        this.notificationsRefreshTimer = null
        this.handleNotificationsSilentRefresh()
      }, 150)
    },

    async handleNotificationsSilentRefresh() {
      await this.loadActivities({ silent: true })
      await this.reloadSubactivitiesForExpandedParents({ silent: true })
      EventBus.$emit('weeklyAssignmentsSilentRefresh')
    },
    resetActivitiesPagination() {
      if (!this.usesActivitiesPagination) {
        return
      }
      this.activitiesPage = 1
    },

    onActivitiesPageChange(page) {
      if (!this.usesActivitiesPagination || page === this.activitiesPage) {
        return
      }
      this.activitiesPage = page
      this.loadActivities()
    },

    buildActivitiesRequestParams() {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        return null
      }

      const loadMode = ACTIVITIES_LOAD_OPTIONS.find((mode) => mode.key === this.activitiesLoadMode)
        || ACTIVITIES_LOAD_OPTIONS[0]

      const request = loadMode.buildRequest(projectId, this.groupId, {
        disciplineMode: this.disciplineMode,
        managerUserId: this.managerUserId,
        page: this.activitiesPage,
        perPage: this.activitiesPerPage,
      })

      if (request && this.isWeeklyWorkMode) {
        request.params = {
          ...this.activityFilters,
          ...request.params,
          with_boq_root: 1,
        }
      } else if (request) {
        request.params = {
          ...this.activityFilters,
          ...request.params,
        }
      }

      if (request && this.isFreeBoqRootMode) {
        request.params = {
          ...request.params,
          with_last_assignment_date: 1,
        }
      }

      if (request && this.usesWeeklyActivityFilter) {
        request.params = {
          ...request.params,
          ...(this.weekStartDate ? { weekly_start: this.weekStartDate } : {}),
          ...(this.weeklyApiEndDate ? { weekly_end: this.weeklyApiEndDate } : {}),
        }
      }

      if (request && this.isTakeoffProgressMode && this.selectedDisciplineId != null) {
        request.params = {
          ...request.params,
          takeoff_discipline_id: this.selectedDisciplineId,
        }
      }

      return request
    },

    calculateProgress(startDate, endDate) {
      const format = 'DD/MM/YYYY'
      const start = moment(startDate, format)
      const end = moment(endDate, format)
      const now = moment()

      if (now.isBefore(start)) {
        return 0
      }

      if (now.isSameOrAfter(end)) {
        return 100
      }

      const totalDuration = end.diff(start, 'milliseconds')
      const elapsedDuration = now.diff(start, 'milliseconds')
      const progress = (elapsedDuration / totalDuration) * 100

      return parseFloat(progress.toFixed(5))
    },

    syncRowPlanningProgress(row) {
      if (!row) {
        return row
      }
      if (this.canProgress) {
        syncPlanningProgressFields(row, this.calculateProgress)
      }
      return syncActivityKpiFields(row)
    },

    mapActivitiesResponse(activities) {
      const mapOptions = {
        canProgress: this.canProgress,
        calculateProgress: this.calculateProgress,
      }

      return activities
        .filter((activity) => !activity.is_subactivity)
        .map((activity) => mapActivityToWeeklyRow(activity, mapOptions))
    },

    async loadActivities(options = {}) {
      const silent = options.silent === true
      const request = this.buildActivitiesRequestParams()
      if (!request) {
        if (!silent) {
          this.activityRows = []
          this.isLoadingActivities = false
        }
        return
      }

      if (!silent) {
        this.isLoadingActivities = true
        this.subactivitiesByParentId = {}
      }

      try {
        const { data } = await axios.get(request.url, {
          params: request.params,
        })

        if (!data || !data.success || !Array.isArray(data.activities)) {
          if (!silent) {
            this.activityRows = []
            this.$notify.error('error_loading_activities')
          }
          return
        }

        this.activityRows = this.mapActivitiesResponse(data.activities).map((row) => {
          const entry = this.findEditedSubactivity(row.id)
          const mapped = entry ? applyRescheduledEntry(row, entry) : initRescheduleBaseline(row)
          if (entry && entry.manager_user_id != null) {
            mapped.manager_id = entry.manager_user_id
          }
          return this.syncRowPlanningProgress(mapped)
        })
        if (this.usesActivitiesPagination) {
          this.activitiesTotal = data.total != null ? data.total : this.activityRows.length
        }
      } catch (error) {
        if (!silent) {
          this.activityRows = []
          this.$notify.error('error_loading_activities')
        }
      } finally {
        if (!silent) {
          this.isLoadingActivities = false
          this.$nextTick(() => {
            this.restoreExpandedActivitiesFromSession()
          })
        }
      }
    },

    isGroupVisible(groupKey) {
      if (groupKey === 'identification') {
        return true
      }
      return !this.effectiveHiddenColumnGroups.includes(groupKey)
    },

    isGroupHidden(groupKey) {
      return this.effectiveHiddenColumnGroups.includes(groupKey)
    },

    toggleColumnGroup(groupKey) {
      if (groupKey === 'identification' || this.hiddenColumnGroups != null) {
        return
      }
      if (this.isGroupHidden(groupKey)) {
        this.localHiddenColumnGroups = this.localHiddenColumnGroups.filter((key) => key !== groupKey)
        return
      }
      this.localHiddenColumnGroups = [...this.localHiddenColumnGroups, groupKey]
    },

    /** La columna expand solo existe en fila 2; el grupo Identificación absorbe su ancho en fila 1. */
    groupColspan(group) {
      if (group.key === 'identification') {
        return group.columns.length + 1 + (this.showActivityActionsColumn ? 1 : 0)
      }
      return group.columns.length
    },

    rowKey(row, rowIndex) {
      return row.id != null ? row.id : rowIndex
    },

    isRowExpanded(row, rowIndex) {
      return this.expandedRowKeys.includes(this.rowKey(row, rowIndex))
    },

    toggleRowDetail(row, rowIndex) {
      const key = this.rowKey(row, rowIndex)
      if (this.isRowExpanded(row, rowIndex)) {
        this.expandedRowKeys = this.expandedRowKeys.filter((k) => k !== key)
        this.clearExpandedSubactivitiesForParent(row)
        this.persistActivitiesSession()
        return
      }
      this.expandedRowKeys = [...this.expandedRowKeys, key]
      if (this.displaySubactivities(row)) {
        this.loadSubactivities(row).then(() => {
          this.persistActivitiesSession()
        })
        return
      }
      this.persistActivitiesSession()
    },

    subactivityExpandKey(parentRow, sub, subIndex) {
      const parentId = parentRow && parentRow.id != null ? parentRow.id : 'parent'
      const subId = sub && sub.id != null ? sub.id : subIndex
      return `${parentId}-${subId}`
    },

    isSubactivityExpanded(parentRow, sub, subIndex) {
      return this.expandedSubactivityKeys.includes(
        this.subactivityExpandKey(parentRow, sub, subIndex)
      )
    },

    toggleSubactivityDetail(parentRow, sub, subIndex) {
      const key = this.subactivityExpandKey(parentRow, sub, subIndex)
      if (this.isSubactivityExpanded(parentRow, sub, subIndex)) {
        this.expandedSubactivityKeys = this.expandedSubactivityKeys.filter((k) => k !== key)
        this.persistActivitiesSession()
        return
      }
      this.expandedSubactivityKeys = [...this.expandedSubactivityKeys, key]
      this.persistActivitiesSession()
    },

    openAdminAssignmentsModal(parentRow, subactivity) {
      if (!this.$refs.weeklyAdminAssignmentsModal) {
        return
      }
      this.$refs.weeklyAdminAssignmentsModal.show({
        subactivity,
        parentActivity: parentRow,
      })
    },

    openCreateSubactivityModal(parentRow) {
      if (this.isFreeBoqRootMode) {
        if (!this.$refs.editActivityModal) {
          return
        }
        this.$refs.editActivityModal.show({
          mode: 'create-subactivity',
          parentActivity: parentRow,
        })
        return
      }

      if (!this.$refs.weeklyCreateSubactivityModal) {
        return
      }
      this.$refs.weeklyCreateSubactivityModal.show({
        parentActivity: parentRow,
      })
    },

    /** Mismos permisos que activities.vue para editar actividad / crear subactividades. */
    canManageActivity(activity) {
      if (!activity) {
        return false
      }
      return (activity.is_creator || activity.is_manager || activity.is_project_manager)
        && activity.status !== ACTIVITY_STATUS_CLOSED
    },

    /** Por ahora la edición en modal solo está disponible en modo free. */
    canEditSubactivityInModal(sub) {
      return this.isFreeBoqRootMode && this.canManageActivity(sub)
    },

    canEditActivityInModal(row) {
      return this.isFreeBoqRootMode && !!row && !row.is_subactivity && this.canManageActivity(row)
    },

    openEditActivityInModal(row) {
      if (!row || row.id == null || !this.canEditActivityInModal(row) || !this.$refs.editActivityModal) {
        return
      }
      this.$refs.editActivityModal.show({
        mode: 'edit',
        activity: { id: row.id, name: row.name },
      })
    },

    hasSubactivityActions(sub) {
      return this.canEditSubactivityInModal(sub) || !this.isFreeBoqRootMode
    },

    openEditSubactivityModal(parentRow, sub) {
      if (!parentRow || !sub || sub.id == null || !this.canEditSubactivityInModal(sub) || !this.$refs.editActivityModal) {
        return
      }
      this.$refs.editActivityModal.show({
        mode: 'edit',
        activity: { id: sub.id, name: sub.name },
        parentActivity: parentRow,
      })
    },

    onEditActivityModalUpdated(payload) {
      const parentId = payload && payload.parent_activity_id != null
        ? payload.parent_activity_id
        : null
      if (parentId == null) {
        this.loadActivities({ silent: true })
        return
      }
      const parentRow = this.activityRows.find((row) => row.id === parentId)
      if (parentRow) {
        this.loadSubactivities(parentRow, { forceReload: true, silent: true })
        return
      }
      this.loadActivities({ silent: true })
    },

    onEditActivityModalCreated(payload) {
      const parentId = payload && payload.parent_activity_id != null
        ? payload.parent_activity_id
        : null

      this.loadActivities().then(() => {
        if (parentId == null) {
          return
        }
        const parentRow = this.activityRows.find((row) => row.id === parentId)
        if (parentRow) {
          this.loadSubactivities(parentRow, { forceReload: true, silent: true })
        }
      })
    },

    /** Mismo modal que en activities.vue al añadir medición recurso. */
    /** Mismos permisos que editar la actividad (el backend valida update sobre la actividad). */
    canEditMeasurements(row) {
      return this.isFreeBoqRootMode && !!row && !row.is_subactivity && this.canManageActivity(row)
    },

    isEditingMeasurements(row) {
      return !!row && this.editingMeasurementsActivityIds.includes(row.id)
    },

    toggleEditMeasurements(row) {
      if (!row || !row.id) {
        return
      }
      if (this.isEditingMeasurements(row)) {
        this.editingMeasurementsActivityIds = this.editingMeasurementsActivityIds.filter((id) => id !== row.id)
        return
      }
      this.editingMeasurementsActivityIds = [...this.editingMeasurementsActivityIds, row.id]
    },

    onMeasurementsEditSaved(row, payload) {
      this.editingMeasurementsActivityIds = this.editingMeasurementsActivityIds.filter((id) => id !== row.id)
      if (payload && payload.changed) {
        // La ponderación / unidades cambian el progreso de la actividad
        this.loadActivities({ silent: true })
      }
    },

    /**
     * Mismo acceso al modo que editar mediciones; cada medición se borra solo si el usuario
     * la creó o es gestor del proyecto (igual que assignment-update-modal en la home de actividad).
     */
    canDeleteMeasurements(row) {
      return this.canEditMeasurements(row)
    },

    isDeletingMeasurements(row) {
      return !!row && this.deletingMeasurementsActivityIds.includes(row.id)
    },

    toggleDeleteMeasurements(row) {
      if (!row || !row.id) {
        return
      }
      if (this.isDeletingMeasurements(row)) {
        this.deletingMeasurementsActivityIds = this.deletingMeasurementsActivityIds.filter((id) => id !== row.id)
        return
      }
      this.deletingMeasurementsActivityIds = [...this.deletingMeasurementsActivityIds, row.id]
    },

    async onMeasurementDeleted(row) {
      if (!row) {
        return
      }
      // Borrar mediciones cambia el número de mediciones y el progreso de la actividad
      await this.loadActivities({ silent: true })
      const updatedRow = this.activityRows.find((activityRow) => activityRow.id === row.id)
      if (!updatedRow || !Number(updatedRow.assignments_num || 0)) {
        this.deletingMeasurementsActivityIds = this.deletingMeasurementsActivityIds.filter((id) => id !== row.id)
      }
    },

    openCreateMeasurementForFreeMode(row) {
      if (!row || !row.id) {
        return
      }
      // En actividad padre, no crear mediciones si ya hay subactividades
      if (!row.is_subactivity && this.freeModeHasSubactivities(row)) {
        return
      }

      this.assignmentModalActivityId = row.id
      const siblingResources = (row.assignments || []).filter(
        (assignment) => assignment.type === 'resource' && assignment.is_header_row == 0
      )

      this.$nextTick(() => {
        const modal = this.$refs.assignment_modal
        if (modal) {
          modal.show(null, siblingResources)
        }
      })
    },

    onSubactivityMeasurementCreated(parentRow) {
      if (parentRow) {
        this.loadSubactivities(parentRow, { forceReload: true, silent: true })
      }
      this.loadActivities({ silent: true })
      EventBus.$emit('weeklyAssignmentsSilentRefresh')
    },

    onAssignmentModalReload() {
      const activityId = this.assignmentModalActivityId
      let parentIdToReloadSubs = null

      if (activityId != null) {
        const parentRow = this.activityRows.find((row) => (
          this.getSubactivitiesForRow(row).some((sub) => sub.id === activityId)
        ))
        if (parentRow) {
          parentIdToReloadSubs = parentRow.id
        }
      }

      this.loadActivities().then(() => {
        EventBus.$emit('weeklyAssignmentsSilentRefresh')

        if (parentIdToReloadSubs == null) {
          return
        }
        const refreshedParent = this.activityRows.find((row) => row.id === parentIdToReloadSubs)
        if (refreshedParent) {
          this.loadSubactivities(refreshedParent, { forceReload: true, silent: true })
        }
      })
    },

    clearExpandedSubactivitiesForParent(parentRow) {
      if (!parentRow || parentRow.id == null) {
        return
      }
      const prefix = `${parentRow.id}-`
      this.expandedSubactivityKeys = this.expandedSubactivityKeys.filter(
        (key) => !String(key).startsWith(prefix)
      )
      removeWeeklyUiSessionSubactivitiesForParent(this.effectiveProjectId, parentRow.id)
    },

    persistActivitiesSession() {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        return
      }

      const activityIdsOnPage = this.activityRows
        .map((row) => row.id)
        .filter((id) => id != null)

      mergeWeeklyUiSessionSection(
        projectId,
        'activities',
        this.expandedRowKeys,
        activityIdsOnPage
      )

      const state = readWeeklyUiSession(projectId)
      const storedSubKeys = new Set(state.subactivities)
      activityIdsOnPage.forEach((parentId) => {
        const prefix = `${parentId}-`
        state.subactivities
          .filter((key) => String(key).startsWith(prefix))
          .forEach((key) => storedSubKeys.delete(key))
      })
      this.expandedSubactivityKeys.forEach((key) => storedSubKeys.add(key))
      state.subactivities = Array.from(storedSubKeys)
      writeWeeklyUiSession(projectId, state)
    },

    async restoreExpandedActivitiesFromSession() {
      const projectId = this.effectiveProjectId
      if (!projectId || !this.activityRows.length) {
        return
      }

      const state = readWeeklyUiSession(projectId)
      const validActivityIds = new Set(
        this.activityRows.map((row) => row.id).filter((id) => id != null)
      )

      this.expandedRowKeys = state.activities.filter((id) => validActivityIds.has(id))

      const loadTasks = []
      this.activityRows.forEach((row, rowIndex) => {
        if (this.isRowExpanded(row, rowIndex) && this.displaySubactivities(row)) {
          loadTasks.push(this.loadSubactivities(row))
        }
      })
      await Promise.all(loadTasks)

      const restoredSubKeys = []
      state.subactivities.forEach((key) => {
        const dashIndex = String(key).indexOf('-')
        if (dashIndex === -1) {
          return
        }
        const parentId = parseInt(String(key).slice(0, dashIndex), 10)
        const subId = parseInt(String(key).slice(dashIndex + 1), 10)
        if (!validActivityIds.has(parentId) || !this.expandedRowKeys.includes(parentId)) {
          return
        }
        const subs = this.subactivitiesByParentId[parentId] || []
        if (subs.some((sub) => sub.id === subId)) {
          restoredSubKeys.push(key)
        }
      })
      this.expandedSubactivityKeys = restoredSubKeys
    },

    /** Subactividades bajo actividad padre (modo subactivity, free con subs, o legacy con subs). */
    displaySubactivities(row) {
      if (!row) {
        return false
      }
      if (this.isFreeBoqRootMode) {
        return this.freeModeHasSubactivities(row)
      }
      if (this.isActivityLikeProgressMode) {
        return false
      }
      if (this.isSubactivityProgressMode) {
        return true
      }
      return Number(row.subactivities_num || 0) > 0
    },

    /** Mediciones directamente bajo actividad padre (activity/takeoff, free sin subs, o legacy sin subs). */
    displayActivityAssignments(row) {
      if (!row) {
        return false
      }
      // free: si hay subactividades, el progreso solo sale de ellas (no mediciones en el padre)
      if (this.isFreeBoqRootMode) {
        return !this.freeModeHasSubactivities(row)
      }
      if (this.isSubactivityProgressMode) {
        return false
      }
      if (this.isActivityLikeProgressMode) {
        return true
      }
      return !this.displaySubactivities(row)
    },

    /** En free: prioriza subactividades si existen o se están cargando. */
    freeModeHasSubactivities(row) {
      if (!row) {
        return false
      }
      if (this.getSubactivitiesForRow(row).length > 0) {
        return true
      }
      if (this.isSubactivitiesLoading(row)) {
        return true
      }
      return Number(row.subactivities_num || 0) > 0
    },

    isSubactivitiesLoading(row) {
      return !!(row && row.id && this.subactivitiesLoading[row.id])
    },

    getSubactivitiesForRow(row) {
      if (!row || !row.id) {
        return []
      }
      return this.subactivitiesByParentId[row.id] || []
    },

    async loadSubactivities(row, options = {}) {
      let forceReload = false
      let silent = false
      if (options === true) {
        forceReload = true
      } else if (options && typeof options === 'object') {
        forceReload = !!options.forceReload
        silent = !!options.silent
      }

      const parentId = row && row.id
      if (!parentId) {
        return
      }
      if (!forceReload && this.subactivitiesByParentId[parentId]) {
        return
      }

      if (!silent) {
        this.subactivitiesLoading = { ...this.subactivitiesLoading, [parentId]: true }
      }

      const params = {
        ...this.activityFilters,
        with_location: 1,
        with_prelations: 1,
      }

      if (this.isWeeklyWorkMode) {
        params.with_boq_root = 1
      }

      if (this.filterSubactivitiesByWeek) {
        if (this.weekStartDate) {
          params.weekly_start = this.weekStartDate
        }
        if (this.weeklyApiEndDate) {
          params.weekly_end = this.weeklyApiEndDate
        }
      }

      if (this.isTakeoffProgressMode && this.selectedDisciplineId != null) {
        params.takeoff_discipline_id = this.selectedDisciplineId
      }

      if (this.managerUserId) {
        params.manager_user_id = this.managerUserId
      }

      try {
        const { data } = await axios.get(`/api/v2/activity/${parentId}/subactivities`, {
          params,
        })

        const mapped = (data && data.success && Array.isArray(data.subactivities))
          ? data.subactivities.map((sub) => {
            const mappedRow = mapSubactivityToWeeklyRow(sub, row)
            const entry = this.findEditedSubactivity(mappedRow.id)
            return entry ? applyRescheduledEntry(mappedRow, entry) : initRescheduleBaseline(mappedRow)
          })
          : []

        this.subactivitiesByParentId = {
          ...this.subactivitiesByParentId,
          [parentId]: mapped,
        }
      } catch (error) {
        if (!silent) {
          this.subactivitiesByParentId = {
            ...this.subactivitiesByParentId,
            [parentId]: [],
          }
        }
      } finally {
        if (!silent) {
          this.subactivitiesLoading = { ...this.subactivitiesLoading, [parentId]: false }
        }
      }
    },

    async reloadSubactivitiesForExpandedParents(options = {}) {
      const silent = options.silent === true
      const tasks = []
      this.activityRows.forEach((row, rowIndex) => {
        if (this.isRowExpanded(row, rowIndex) && this.displaySubactivities(row)) {
          tasks.push(this.loadSubactivities(row, { forceReload: true, silent }))
        }
      })
      await Promise.all(tasks)
    },

    subactivityRowKey(sub, subIndex) {
      return sub.id != null ? sub.id : subIndex
    },

    parentDateMin(parentRow) {
      return parseApiDate(parentRow.current_start_date)
    },

    parentDateMax(parentRow) {
      return parseApiDate(parentRow.current_end_date)
    },

    subStartMaxDate(parentRow, sub) {
      const parentMax = this.parentDateMax(parentRow)
      if (sub._endDate && parentMax) {
        return sub._endDate < parentMax ? sub._endDate : parentMax
      }
      return parentMax
    },

    subEndMinDate(parentRow, sub) {
      const parentMin = this.parentDateMin(parentRow)
      if (sub._startDate && parentMin) {
        return sub._startDate > parentMin ? sub._startDate : parentMin
      }
      return parentMin
    },

    onSubStartDateChange(parentRow, sub, value) {
      sub._startDate = value
      sub.current_start_date = formatApiDate(value)
      const parentMax = this.parentDateMax(parentRow)
      if (sub._endDate && parentMax && sub._endDate > parentMax) {
        sub._endDate = parentMax
        sub.current_end_date = formatApiDate(parentMax)
      }
      if (sub._endDate && sub._startDate && sub._endDate < sub._startDate) {
        sub._endDate = sub._startDate
        sub.current_end_date = formatApiDate(sub._startDate)
      }
      this.markSubactivityRescheduled(parentRow, sub)
    },

    onSubEndDateChange(parentRow, sub, value) {
      sub._endDate = value
      sub.current_end_date = formatApiDate(value)
      const parentMin = this.parentDateMin(parentRow)
      if (sub._startDate && parentMin && sub._startDate < parentMin) {
        sub._startDate = parentMin
        sub.current_start_date = formatApiDate(parentMin)
      }
      if (sub._startDate && sub._endDate && sub._endDate < sub._startDate) {
        sub._startDate = sub._endDate
        sub.current_start_date = formatApiDate(sub._endDate)
      }
      this.markSubactivityRescheduled(parentRow, sub)
    },

    repositionManagerAutocomplete(sub) {
      if (!sub || sub.id == null) {
        return
      }
      const ref = this.$refs[`managerAc_${sub.id}`]
      const component = Array.isArray(ref) ? ref[0] : ref
      if (!component) {
        return
      }
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          if (typeof component.updateAppendToBody === 'function') {
            component.updateAppendToBody()
          }
          if (typeof component.calcDropdownInViewportVertical === 'function') {
            component.calcDropdownInViewportVertical()
          }
        })
      })
    },

    onManagerAutocompleteActive(sub, active) {
      if (active) {
        this.$nextTick(() => {
          this.repositionManagerAutocomplete(sub)
        })
      }
    },

    onManagerFocus(sub) {
      if (!sub) {
        return
      }
      const query = sub.manager_search != null ? String(sub.manager_search).trim() : ''
      if (query.length >= 3) {
        this.onManagerTyping(sub, query)
      }
      this.$nextTick(() => {
        this.repositionManagerAutocomplete(sub)
      })
    },

    async onManagerTyping(sub, value) {
      if (!sub || sub.id == null) {
        return
      }
      const query = value != null ? String(value).trim() : ''
      if (!query || query.length < 3) {
        this.$set(sub, 'managerOptions', [])
        return
      }
      if (!this.effectiveProjectId) {
        return
      }
      const { url, params } = buildManagerSearchRequest(this.effectiveProjectId, query)
      try {
        const { data } = await axios.get(url, { params })
        const users = (data && data.success && Array.isArray(data.users))
          ? data.users
          : (data && Array.isArray(data.users) ? data.users : [])
        const mapped = mapManagerSearchResults(users)
        this.$set(sub, 'managerOptions', mapped)
        this.$nextTick(() => {
          this.repositionManagerAutocomplete(sub)
        })
      } catch (error) {
        this.$set(sub, 'managerOptions', [])
      }
    },

    onManagerSelected(sub, user) {
      if (!sub || !user) {
        return
      }
      sub.manager_id = user.id
      sub.manager = user.fullname || buildManagerLabel(user)
      sub.manager_search = sub.manager || ''
      this.$set(sub, 'managerOptions', [])
      if (this.isActivityLikeProgressMode && this.activityRows.some((row) => row.id === sub.id)) {
        this.markActivityRescheduled(sub)
        return
      }
      this.markSubactivityRescheduled(this.findParentRowForSub(sub), sub)
    },

    findParentRowForSub(sub) {
      if (!sub || sub.id == null) {
        return null
      }
      return this.activityRows.find((row) => {
        const subs = this.subactivitiesByParentId[row.id]
        return Array.isArray(subs) && subs.some((item) => item.id === sub.id)
      }) || null
    },

    findEditedSubactivity(activityId) {
      if (activityId == null || !Array.isArray(this.editedSubactivities)) {
        return null
      }
      return this.editedSubactivities.find((item) => item.id === activityId) || null
    },

    applyEditedSubactivitiesToLoadedSubactivities() {
      const nextMap = { ...this.subactivitiesByParentId }
      let changed = false

      Object.keys(nextMap).forEach((parentId) => {
        const subs = nextMap[parentId]
        if (!Array.isArray(subs) || !subs.length) {
          return
        }

        nextMap[parentId] = subs.map((sub) => {
          const baseline = sub._rescheduleBaseline || {
            current_start_date: sub.current_start_date,
            current_end_date: sub.current_end_date,
            people: sub.people,
            manager_id: sub.manager_id,
            status: sub.status,
          }
          const entry = this.findEditedSubactivity(sub.id)
          if (entry) {
            return applyRescheduledEntry({ ...sub, _rescheduleBaseline: baseline }, entry)
          }
          return {
            ...sub,
            status: baseline.status,
            _rescheduleBaseline: baseline,
          }
        })
        changed = true
      })

      if (changed) {
        this.subactivitiesByParentId = nextMap
      }
    },

    applyEditedActivitiesToRows() {
      if (!this.isActivityLikeProgressMode || !Array.isArray(this.activityRows) || !this.activityRows.length) {
        return
      }

      this.activityRows = this.activityRows.map((row) => {
        const baseline = row._rescheduleBaseline || {
          current_start_date: row.current_start_date,
          current_end_date: row.current_end_date,
          people: row.people,
          manager_id: row.manager_id,
          status: row.status,
        }
        const entry = this.findEditedSubactivity(row.id)
        if (entry) {
          return this.syncRowPlanningProgress(
            applyRescheduledEntry({ ...row, _rescheduleBaseline: baseline }, entry)
          )
        }
        return this.syncRowPlanningProgress({
          ...row,
          status: baseline.status,
          _rescheduleBaseline: baseline,
        })
      })
    },

    toggleActivityPendingPin(row) {
      this.toggleSubactivityPendingPin(null, row)
    },

    markActivityRescheduled(row) {
      this.markSubactivityRescheduled(null, row)
    },

    activityPlannedDateMin(row) {
      return parseApiDate(row.scheduled_start_date)
    },

    activityPlannedDateMax(row) {
      return parseApiDate(row.scheduled_end_date)
    },

    activityStartMaxDate(row) {
      const plannedMax = this.activityPlannedDateMax(row)
      if (row._endDate && plannedMax) {
        return row._endDate < plannedMax ? row._endDate : plannedMax
      }
      return row._endDate || plannedMax
    },

    activityEndMinDate(row) {
      const plannedMin = this.activityPlannedDateMin(row)
      if (row._startDate && plannedMin) {
        return row._startDate > plannedMin ? row._startDate : plannedMin
      }
      return row._startDate || plannedMin
    },

    onActivityStartDateChange(row, value) {
      row._startDate = value
      row.current_start_date = formatApiDate(value)
      row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      const plannedMax = this.activityPlannedDateMax(row)
      if (row._endDate && plannedMax && row._endDate > plannedMax) {
        row._endDate = plannedMax
        row.current_end_date = formatApiDate(plannedMax)
        row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      }
      if (row._endDate && row._startDate && row._endDate < row._startDate) {
        row._endDate = row._startDate
        row.current_end_date = formatApiDate(row._startDate)
        row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      }
      this.syncRowPlanningProgress(row)
      this.markActivityRescheduled(row)
    },

    onActivityEndDateChange(row, value) {
      row._endDate = value
      row.current_end_date = formatApiDate(value)
      row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      const plannedMin = this.activityPlannedDateMin(row)
      if (row._startDate && plannedMin && row._startDate < plannedMin) {
        row._startDate = plannedMin
        row.current_start_date = formatApiDate(plannedMin)
        row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      }
      if (row._startDate && row._endDate && row._endDate < row._startDate) {
        row._startDate = row._endDate
        row.current_start_date = formatApiDate(row._endDate)
        row.current_duration_days = resolveDurationDays(row.current_start_date, row.current_end_date)
      }
      this.syncRowPlanningProgress(row)
      this.markActivityRescheduled(row)
    },

    toggleSubactivityPendingPin(parentRow, sub) {
      if (!this.rescheduleMode || !sub || sub.id == null) {
        return
      }

      if (!sub._rescheduleBaseline) {
        initRescheduleBaseline(sub)
      }

      if (this.isPendingRescheduling(sub)) {
        sub.status = sub._rescheduleBaseline.status
      } else {
        sub.status = ACTIVITY_STATUS_PENDING_RESCHEDULING
      }

      this.syncEditedSubactivity(parentRow, sub)
    },

    markSubactivityRescheduled(parentRow, sub) {
      this.syncEditedSubactivity(parentRow, sub)
    },

    syncEditedSubactivity(parentRow, sub) {
      if (!this.rescheduleMode || !sub || sub.id == null) {
        return
      }

      if (!sub._rescheduleBaseline) {
        initRescheduleBaseline(sub)
      }

      const baseline = sub._rescheduleBaseline
      const peopleRaw = sub.people
      const people = peopleRaw != null && peopleRaw !== '' && !Number.isNaN(parseInt(peopleRaw, 10))
        ? parseInt(peopleRaw, 10)
        : null

      const hasFieldChanges = (
        baseline.current_start_date !== sub.current_start_date
        || baseline.current_end_date !== sub.current_end_date
        || baseline.people !== people
        || baseline.manager_id !== sub.manager_id
      )
      const hasStatusChange = sub.status === ACTIVITY_STATUS_PENDING_RESCHEDULING

      if (!hasFieldChanges && !hasStatusChange) {
        sub.status = baseline.status
        this.$emit('remove-rescheduled-activity', sub.id)
        return
      }

      this.$emit('update-rescheduled-activity', {
        id: sub.id,
        current_start_date: sub.current_start_date,
        current_end_date: sub.current_end_date,
        people,
        manager_user_id: sub.manager_id,
        status: sub.status,
      })
    },

    isPendingRescheduling(sub) {
      return sub && sub.status === ACTIVITY_STATUS_PENDING_RESCHEDULING
    },

    shouldShowSubactivityStatus(sub) {
      return sub && sub.status != null && sub.status !== ''
    },

    subactivityStatusLabel(sub) {
      if (!sub || sub.status == null || sub.status === '') {
        return ''
      }
      const key = ACTIVITY_STATUS_LABEL_KEYS[sub.status]
      return key ? this.$t(key) : ''
    },

    subactivityStatusClass(sub) {
      if (!sub || sub.status == null || sub.status === '') {
        return 'activities-weekly-subtable__status--default'
      }
      const suffix = ACTIVITY_STATUS_CSS_SUFFIX[sub.status] || 'default'
      return `activities-weekly-subtable__status--${suffix}`
    },

    canNavigateToRow(row) {
      return row && row.without_access !== 1
    },

    onNameClicked(row) {
      if (!this.canNavigateToRow(row)) {
        return
      }
      this.onRowClicked(row.is_subactivity, row.parent_id, row.id)
    },

    onSubactivityNameClicked(parentRow, sub) {
      if (!sub || !sub.access) {
        return
      }
      this.onRowClicked(sub.is_subactivity, parentRow.id, sub.id, sub.access)
    },

    onRowClicked(isSubactivity, activityId, subactivityId, access = true) {
      if (!access) {
        return
      }
      if (!isSubactivity) {
        this.$router.push({ name: 'activity.home', params: { activity_id: activityId } })
        return
      }
      this.$router.push({
        name: 'subactivity.home',
        params: { activity_id: activityId, subactivity_id: subactivityId },
      })
    },

    colorGroup(group) {
      const defaultColor = '#F29B57'
      if (!group || !group.configs) {
        return defaultColor
      }
      try {
        const configs = typeof group.configs === 'string'
          ? JSON.parse(group.configs)
          : group.configs
        return configs && configs.color ? configs.color : defaultColor
      } catch (e) {
        return defaultColor
      }
    },

    nameGroup(group) {
      if (!group || !group.name) {
        return ''
      }
      return group.name.substring(0, 2).toUpperCase()
    },

    groupTooltip(row) {
      return row.full_path || (row.group ? row.group.name : '')
    },

    relationCodes(value) {
      if (value == null || value === '') {
        return []
      }
      if (Array.isArray(value)) {
        return value.filter((code) => code != null && String(code).trim() !== '')
      }
      return String(value)
        .split(/[,;|]/)
        .map((code) => code.trim())
        .filter(Boolean)
    },

    relationExpandKey(row, kind) {
      return `${row && row.id != null ? row.id : 'row'}:${kind}`
    },

    isRelationExpanded(row, kind) {
      return this.expandedRelationKeys.includes(this.relationExpandKey(row, kind))
    },

    toggleRelationExpand(row, kind) {
      const key = this.relationExpandKey(row, kind)
      if (this.expandedRelationKeys.includes(key)) {
        this.expandedRelationKeys = this.expandedRelationKeys.filter((item) => item !== key)
        return
      }
      this.expandedRelationKeys = [...this.expandedRelationKeys, key]
    },

    visibleRelationCodes(row, kind) {
      const codes = this.relationCodes(row && row[kind])
      if (this.isRelationExpanded(row, kind) || codes.length <= RELATION_CODES_PREVIEW) {
        return codes
      }
      return codes.slice(0, RELATION_CODES_PREVIEW)
    },

    hiddenRelationCount(row, kind) {
      const total = this.relationCodes(row && row[kind]).length
      if (total <= RELATION_CODES_PREVIEW) {
        return 0
      }
      return total - RELATION_CODES_PREVIEW
    },

    isCellEmpty(value) {
      if (value === null || value === undefined || value === '') {
        return true
      }
      return false
    },

    isManuallyCreated(row) {
      return !!row && isManuallyCreatedCode(row.code)
    },

    formatBoqTrackingQuantity(value, row) {
      const formatted = formatProjectFigure(value, this.project)
      const text = formatted === '' ? value : formatted
      const unit = row && row.primary_boq_unit
      if (unit) {
        return `${text} ${unit}`
      }
      return text
    },

    formatDuration(value) {
      if (this.isCellEmpty(value)) {
        return ''
      }
      const raw = String(value).trim()
      if (/\bd\b/i.test(raw)) {
        return raw.replace(/\s*d\s*$/i, ' d')
      }
      const days = parseFloat(raw)
      if (Number.isNaN(days)) {
        return raw
      }
      return `${Math.round(days)} d`
    },

    floatColorClass(value) {
      const days = parseFloat(String(value).replace(/[^\d.-]/g, ''))
      if (Number.isNaN(days)) {
        return ''
      }
      if (days >= 8) {
        return 'font-bold text-grey-darkest'
      }
      if (days >= 4 || days === 0) {
        return 'text-orange'
      }
      return 'font-bold text-red'
    },

    formatKpi(value) {
      const kpi = parseFloat(value)
      if (Number.isNaN(kpi)) {
        return value
      }
      return kpi.toFixed(2)
    },

    kpiPillClass(value) {
      const kpi = parseFloat(value)
      if (Number.isNaN(kpi)) {
        return ''
      }
      if (kpi >= 1) {
        return 'activities-weekly-table__kpi-pill--green'
      }
      if (kpi >= 0.85) {
        return 'activities-weekly-table__kpi-pill--yellow'
      }
      return 'activities-weekly-table__kpi-pill--red'
    },

    measurementsBadgeCount(row) {
      if (this.isWeeklyWorkMode) {
        const roots = Array.isArray(row.boq_roots) ? row.boq_roots : []
        return roots.length
      }

      if (this.isSubactivityProgressMode) {
        const count = row.subactivities_num != null ? row.subactivities_num : 0
        return Number(count) > 0 ? Number(count) : 0
      }
      const count = row.measurements_count != null
        ? row.measurements_count
        : row.measurementsCount
      if (count == null || count === '' || Number(count) <= 0) {
        return 0
      }
      return Number(count)
    },

    showMeasurementsIcon(row) {
      if (this.isFreeBoqRootMode) {
        return false
      }
      if (this.isWeeklyWorkMode) {
        return true
      }
      if (this.isSubactivityProgressMode) {
        return true
      }
      return this.measurementsBadgeCount(row) > 0
    },

    canOpenLinkedBoqRoots(row) {
      return this.isWeeklyWorkMode && this.measurementsBadgeCount(row) > 0
    },

    openLinkedBoqRootsModal(row) {
      if (!this.canOpenLinkedBoqRoots(row)) {
        return
      }
      this.$refs.weeklyLinkedBoqRootsModal.show({
        activityId: row.id,
        projectId: this.effectiveProjectId,
        activityName: row.name,
        boqRoots: row.boq_roots,
      })
    },

    openEditActivityModal(row) {
      if (!this.isActivityLikeProgressMode || !row || row.id == null) {
        return
      }
      if (this.$refs.weeklyEditActivityModal) {
        this.$refs.weeklyEditActivityModal.show({ row })
      }
    },

    onWeeklyActivityEdited() {
      this.loadActivities({ silent: true })
    },

    onWeeklySubactivityAssignmentsSaved(payload = {}) {
      const parentId = payload.parent_id
      if (parentId) {
        const parentRow = this.activityRows.find((row) => row.id === parentId)
        if (parentRow) {
          this.loadSubactivities(parentRow, { forceReload: true, silent: true })
          return
        }
      }
      this.loadActivities({ silent: true })
    },

    onBoqPrimaryChanged({ activityId, boqRootId }) {
      const row = this.activityRows.find((item) => item.id === activityId)
      if (!row || !Array.isArray(row.boq_roots)) {
        this.loadActivities()
        return
      }

      row.boq_roots = row.boq_roots.map((root) => ({
        ...root,
        is_primary: root.id === boqRootId,
      }))
      row.physical_quantity = resolvePrimaryBoqRootQuantity(row.boq_roots)
      row.primary_boq_unit = resolvePrimaryBoqRootUnit(row.boq_roots)
      syncActivityKpiFields(row)
      this.syncRowPlanningProgress(row)
    },

    measurementsTooltip(row) {
      const count = this.measurementsBadgeCount(row)
      if (this.isWeeklyWorkMode) {
        if (count > 0) {
          return `${this.$t('weekly_linked_boq_roots_title')} (${count})`
        }
        return this.$t('weekly_linked_boq_roots_title')
      }
      if (this.isSubactivityProgressMode) {
        if (count > 0) {
          return this.$t('subactivities') + ` (${count})`
        }
        return this.$t('subactivities')
      }
      if (count > 0) {
        return this.$t('measurements') + ` (${count})`
      }
      return this.$t('measurements')
    },

  },
}
</script>

<style scoped>
.activities-weekly-table__group-chip {
  transition: opacity 0.15s ease;
}

.activities-weekly-table__group-chip--hidden {
  opacity: 0.45;
}

.activities-weekly-table__header-cell {
  border: 1px solid #5b80be;
}

/* Fila 1: títulos de grupo */
.activities-weekly-table__group--identification {
  background-color: #2c426c;
}

.activities-weekly-table__group--tracking {
  background-color: #3a5689;
}

.activities-weekly-table__group--planned-advance {
  background-color: #1e3a6b;
}

.activities-weekly-table__group--scheduled-advance {
  background-color: #2d5d3f;
}

.activities-weekly-table__group--real {
  background-color: #5a4a8a;
}

/* Fila 2: todas las columnas */
.activities-weekly-table__column-header {
  background-color: #5b80be;
}

.activities-weekly-table__expand-header {
  min-width: 2.5rem;
}

.activities-weekly-table__actions-col {
  min-width: 2.5rem;
  width: 2.5rem;
}

.activities-weekly-table__data-row:hover > td {
  background-color: #eff8ff;
}

.activities-weekly-table__row--expanded > td {
  background-color: #eef3fb;
}

/* Actividades creadas desde el modal (no importadas); al expandir prevalece la franja del expandido */
.activities-weekly-table__row--manual > td:first-child {
  box-shadow: inset 4px 0 0 #9561e2;
}

.activities-weekly-table__row--expanded > td:first-child {
  box-shadow: inset 4px 0 0 #5b80be;
}

.activities-weekly-table__row--expanded:hover > td {
  background-color: #eef3fb;
}

.activities-weekly-table__scroll--reschedule {
  overflow-x: auto;
  overflow-y: visible;
}

.activities-weekly-table--reschedule {
  overflow: visible;
}

.activities-weekly-table__detail-row > td {
  overflow: visible;
}

.activities-weekly-table__detail-cell:has(.activities-weekly-table__detail-panel--reschedule),
.activities-weekly-table--reschedule .activities-weekly-table__detail-cell {
  overflow: visible;
}

/* Las tablas hijas ocupan su contenido, pero como mínimo el ancho visible de la tabla padre */
.activities-weekly-table__detail-panel {
  width: max-content;
  min-width: calc(var(--weekly-visible-width, 100%) - 2px);
  max-width: 100%;
  min-height: 0;
  padding: 0;
  border-left: 4px solid #5b80be;
  border-bottom: 4px solid #5b80be;
}

.activities-weekly-table__detail-panel--reschedule,
.activities-weekly-table__detail-panel--reschedule .activities-weekly-subtable__scroll--reschedule {
  overflow: visible;
}

.activities-weekly-subtable__scroll--reschedule {
  overflow-x: auto;
  overflow-y: visible;
}

.activities-weekly-subtable--reschedule,
.activities-weekly-subtable--reschedule thead,
.activities-weekly-subtable--reschedule tbody,
.activities-weekly-subtable--reschedule tr,
.activities-weekly-subtable--reschedule td,
.activities-weekly-subtable--reschedule th {
  overflow: visible;
}

.activities-weekly-subtable__cell--manager {
  position: relative;
  overflow: visible;
  vertical-align: middle;
}

.activities-weekly-subtable__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #eef3fb;
  border-bottom: 1px solid #c5d4eb;
}

.activities-weekly-subtable__body {
  min-height: 0;
  padding: 0.75rem;
}

.activities-weekly-subtable__body .activities-weekly-subtable__toolbar {
  margin-bottom: 0.75rem;
  border: 1px solid #c5d4eb;
  border-radius: 0.375rem;
}

.activities-weekly-subtable__section {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  background-color: #fff;
}

.activities-weekly-subtable__section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(90deg, #2c426c 0%, #3b66bc 100%);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.activities-weekly-subtable__section-icon {
  flex-shrink: 0;
}

.activities-weekly-subtable__section-title {
  flex: 1;
  min-width: 0;
}

.activities-weekly-subtable__level-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  background-color: #2c426c;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
}

.activities-weekly-subtable__count {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3b66bc;
}

.activities-weekly-subtable__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #3b66bc;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.activities-weekly-subtable__add-btn:hover {
  background-color: #eef3fb;
}

.activities-weekly-subtable__add-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.activities-weekly-subtable__add-btn:disabled:hover {
  background-color: #fff;
}

.activities-weekly-subtable__header {
  padding: 0.375rem 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #fff;
  background-color: #5b80be;
  border: 1px solid #5b80be;
  white-space: nowrap;
}

.activities-weekly-subtable__header--left {
  text-align: left;
}

.activities-weekly-subtable__expand-col,
.activities-weekly-subtable__actions-col {
  min-width: 2.5rem;
  width: 2.5rem;
}

.activities-weekly-subtable__cell {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  color: #1f2937;
  vertical-align: middle;
  background-color: #fff;
}

.activities-weekly-subtable__name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 14rem;
  max-width: 22rem;
}

.activities-weekly-subtable__name-cell--stacked {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
}

.activities-weekly-subtable__name-top {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  min-width: 0;
}

.activities-weekly-subtable__pin-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  padding: 0;
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #9ca3af;
  flex-shrink: 0;
  cursor: pointer;
  line-height: 1;
}

.activities-weekly-subtable__pin-icon:hover {
  border-color: #fcd34d;
  color: #b45309;
}

.activities-weekly-subtable__pin-icon--pending {
  border-color: #fcd34d;
  background-color: #fef3c7;
  color: #92400e;
}

.activities-weekly-subtable__pin-icon--pending:hover {
  border-color: #f59e0b;
  background-color: #fde68a;
}

.activities-weekly-subtable__name-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activities-weekly-subtable__status {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  border: 1px solid transparent;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.2;
}

.activities-weekly-subtable__status--assigned {
  background-color: #b8c2cc;
  color: #fff;
}

.activities-weekly-subtable__status--running {
  background-color: #3490dc;
  color: #fff;
}

.activities-weekly-subtable__status--paused {
  background-color: #f6993f;
  color: #fff;
}

.activities-weekly-subtable__status--issue {
  background-color: #e3342f;
  color: #fff;
}

.activities-weekly-subtable__status--done {
  background-color: #6574cd;
  color: #fff;
}

.activities-weekly-subtable__status--reopened {
  background-color: #9561e2;
  color: #fff;
}

.activities-weekly-subtable__status--closed {
  background-color: #38c172;
  color: #fff;
}

.activities-weekly-subtable__status--delayed-start {
  background-color: #fc8a18;
  color: #fff;
}

.activities-weekly-subtable__status--delayed {
  background-color: #f9c74f;
  color: #1f2937;
}

.activities-weekly-subtable__status--pending-rescheduling {
  background-color: #fef3c7;
  border-color: #fcd34d;
  color: #92400e;
}

.activities-weekly-subtable__status--default {
  background-color: #e5e7eb;
  color: #374151;
}

.activities-weekly-subtable__row--expanded > td {
  background-color: #eef3fb;
}

.activities-weekly-subtable__row--manual > td.activities-weekly-subtable__expand-col {
  box-shadow: inset 4px 0 0 #9561e2;
}

.activities-weekly-subtable__row--expanded > td.activities-weekly-subtable__expand-col {
  box-shadow: inset 4px 0 0 #5b80be;
}

.activities-weekly-subtable__row--expanded.activities-weekly-subtable__row--level-3 > td {
  background-color: #e8f5ec;
}

.activities-weekly-subtable__row--expanded.activities-weekly-subtable__row--level-3 > td.activities-weekly-subtable__expand-col {
  box-shadow: inset 4px 0 0 #3d8b5a;
}

.activities-weekly-subtable__detail-panel {
  border-left: 4px solid #5b80be;
  border-bottom: 4px solid #5b80be;
}

.activities-weekly-subtable__detail-panel--level-3 {
  border-left-color: #3d8b5a;
  border-bottom-color: #3d8b5a;
}

.activities-weekly-subtable__toolbar--level-3 {
  background-color: #e8f5ec;
  border-bottom: 1px solid #a8d5b5;
}

.activities-weekly-subtable__level-badge--green {
  background-color: #2f6b45;
}

.activities-weekly-subtable__add-btn--green {
  border-color: #3d8b5a;
  color: #2f6b45;
}

.activities-weekly-subtable__add-btn--green:hover {
  background-color: #e8f5ec;
}

.activities-weekly-subtable__add-btn--danger {
  border-color: #dc2626;
  color: #dc2626;
}

.activities-weekly-subtable__add-btn--danger:hover {
  background-color: #fef2f2;
}

.activities-weekly-subtable__add-btn--red {
  border-color: #dc2626;
  color: #fff;
  background-color: #dc2626;
}

.activities-weekly-subtable__add-btn--red:hover {
  background-color: #b91c1c;
}

.activities-weekly-table__name-td {
  min-width: 14rem;
  white-space: normal;
}

.activities-weekly-table__name-cell {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
}

.activities-weekly-table__name-text {
  flex: 1;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

.activities-weekly-table__name-text--clickable {
  cursor: pointer;
  color: #3b66bc;
  font-weight: 600;
}

.activities-weekly-table__name-text--clickable:hover {
  text-decoration: underline;
}

.activities-weekly-table__name-top {
  align-items: flex-start;
}

.activities-weekly-table__ruler-icon--clickable {
  cursor: pointer;
}

.activities-weekly-table__ruler-icon--clickable:hover .inline-flex {
  border-color: #3b66bc;
  background-color: #eef3fb;
}

.activities-weekly-table__edit-icon {
  cursor: pointer;
  padding: 0;
  border: 0;
  background: transparent;
}

.activities-weekly-table__edit-icon:hover .inline-flex {
  border-color: #3b66bc;
  background-color: #eef3fb;
}

.activities-weekly-table__ruler-badge {
  position: absolute;
  top: -0.2rem;
  right: -0.2rem;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.2rem;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1rem;
  text-align: center;
  color: #fff;
  background-color: #5b80be;
  border-radius: 9999px;
}

.activities-weekly-table__relations-td {
  max-width: 10rem;
}

.activities-weekly-table__relations-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  justify-content: center;
}

.activities-weekly-table__chip {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  font-size: 0.65rem;
  line-height: 1.2;
  color: #2c426c;
  background-color: #f3f4f6;
  border: 1px solid #b8cce8;
  border-radius: 0.25rem;
}

.activities-weekly-table__chip--more {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-weight: 700;
  color: #1d4ed8;
  background-color: #eff6ff;
  border: 1px dashed #3b82f6;
  cursor: pointer;
}

.activities-weekly-table__chip--more:hover {
  background-color: #dbeafe;
  border-color: #2563eb;
}

.activities-weekly-table__chip-more-count {
  display: inline-block;
  min-width: 1.1rem;
  padding: 0 0.2rem;
  font-size: 0.6rem;
  line-height: 1.15;
  text-align: center;
  color: #fff;
  background-color: #2563eb;
  border-radius: 9999px;
}

.activities-weekly-table__weight {
  font-weight: 700;
  color: #3a5689;
}

.activities-weekly-table__empty {
  display: inline-block;
  color: #c4c4c4;
  font-size: 0.75rem;
  line-height: 1;
  text-transform: lowercase;
}

.activities-weekly-table__kpi-pill {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.2;
  border-radius: 0.25rem;
  border: 1px solid transparent;
}

.activities-weekly-table__kpi-pill--green {
  color: #2d6a3e;
  background-color: #e8f5ec;
  border-color: #7bc492;
}

.activities-weekly-table__kpi-pill--yellow {
  color: #9a6700;
  background-color: #fff8e6;
  border-color: #e6b422;
}

.activities-weekly-table__kpi-pill--red {
  color: #b42318;
  background-color: #fdeeed;
  border-color: #f1998e;
}

.activities-weekly-subtable__inline-input {
  max-width: 4.5rem;
  margin: 0 auto;
}

.activities-weekly-subtable__inline-input >>> .input {
  text-align: center;
  height: 1.75rem;
  font-size: 0.75rem;
}

.activities-weekly-subtable__inline-datepicker {
  min-width: 7.5rem;
}

.activities-weekly-subtable__inline-datepicker >>> .input {
  height: 1.75rem;
  font-size: 0.75rem;
}

.activities-weekly-subtable__inline-autocomplete {
  min-width: 10rem;
}

.activities-weekly-subtable__inline-autocomplete >>> .input {
  height: 1.75rem;
  font-size: 0.75rem;
}
</style>

<style>
/* append-to-body: flotar sobre detail panel, header sticky (z-10) y tablas anidadas */
body > .autocomplete.control,
body > div > .autocomplete.control {
  z-index: 200 !important;
}

body > .autocomplete.control .dropdown-menu,
body > div > .autocomplete.control .dropdown-menu {
  z-index: 200 !important;
}

body > .datepicker.control,
body > div > .dropdown .dropdown-menu {
  z-index: 120 !important;
}
</style>
